import { clientLogger } from '@/lib/clientLogger';
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { ENV } from '@/config/environment';
import { authService, authReady } from './firebase';
import { setServerLockedOrg, DENY_SUBSCRIPTION_LOCKED } from './subscriptionSignal';

/** Pull the human-readable message out of a typical axios/API error body. */
export function apiErrorMessage(err: unknown, fallback = 'Something went wrong'): string {
  const data = (err as { response?: { data?: Record<string, unknown> | string } })?.response?.data;
  // `BadRequest("...")` sends the reason as a bare string body. Skip HTML error pages from proxies.
  if (typeof data === 'string') {
    const text = data.trim();
    if (text && !text.startsWith('<')) return text;
  }
  const body = typeof data === 'object' ? data : undefined;
  const raw = body?.details ?? body?.error ?? body?.message ?? body?.title
    ?? (err as { message?: string })?.message;
  if (Array.isArray(raw)) {
    const joined = raw.filter((x): x is string => typeof x === 'string' && x.trim().length > 0).join(', ');
    return joined || fallback;
  }
  if (typeof raw === 'string' && raw.trim()) return raw;
  return fallback;
}
import {
  CompleteUploadItem,
  AttachmentUpdate,
  CompleteExpenseUploadItem,
  ContactDTO,
  CreateInvoiceDto,
  ExpenseAttachmentInfo,
  JobAttachmentInfo,
  PrepareUploadResponse,
  MmsPrepareUploadResponse,
  JobMergePatchDTO,
  JobStatusDTO,
  UpdateCompanyInvoiceDTO,
  CompanyInvoiceDTO,
  InvoiceItemAutocompleteDto,
  InvoiceItemFullDto,
  ContactLookupResultDTO,
  JobTextAnalysisDTO,
  CompanyPaymentMethodConfigDTO,
  UpdateCompanyPaymentMethodConfigsDTO,
  // Compensation types
  CompensationResultDTO,
  OverrideCompensationDTO,
  ProfitResultDTO,
  JobFinancialSummaryDTO,
  TechnicianCompensationPlan,
  CreateTechnicianPlanDTO,
  UpdateTechnicianPlanDTO,
  // Balance settlement types
  JobDTO,
  JobListItemDTO,
  TechnicianBalanceDTO,
  BatchSettleResponseDTO,
  UpdateJobTypeDTO,
} from '@/types';
import { getVoipXTimezoneHeaderValue } from './dateUtils';

const API_BASE_URL = ENV.API.BASE_URL;

// ──────────────────────────────────────────────────────────────────────────────
// Portal Token Response Types
// ──────────────────────────────────────────────────────────────────────────────

interface InvoicePortalTokenResponse {
  token: string;
  token_id: string;
  organization_id: string;
  company_id: string;
  invoice_id: string;
  short_code?: string;
}

interface EstimatePortalTokenResponse {
  token: string;
  token_id: string;
  organization_id: string;
  company_id: string;
  estimate_id: string;
  short_code?: string;
}

// Per-flow email template (company override or built-in default)
export interface EmailTemplateConfig {
  flow: string;
  subject: string;
  body: string;
  button_label: string | null;
  is_custom: boolean;
  variables: string[];
  default_subject: string;
  default_body: string;
  default_button_label: string | null;
}

// Per-flow SMS template (company override or built-in default). DispatchVoip serializes camelCase.
// `body` is only the editable middle - the "{Company}: " prefix and `optOutText` are added by
// composeSms() in @/lib/smsTemplate and are not editable.
export interface SmsTemplateConfig {
  flow: string;
  body: string;
  isCustom: boolean;
  variables: string[];
  defaultBody: string;
  optOutText: string;
}

// ──────────────────────────────────────────────────────────────────────────────

class ApiService {
  private api: AxiosInstance;
  /** Only bounded native-call cleanup requests carry this context. The web caller is unchanged. */
  private voiceCleanupContexts = new WeakMap<object, { orgId: string; companyId: string }>();

  private assertRequestCurrent(config: AxiosRequestConfig): void {
    if (config.signal?.aborted) throw new axios.CanceledError('Request cancelled.');
    const context = config.signal && this.voiceCleanupContexts.get(config.signal);
    if (context && (context.orgId !== this.orgId || context.companyId !== this.companyId)) {
      throw new axios.CanceledError('Call cleanup context changed.');
    }
  }
  private companyId: string | null = null;
  private orgId: string | null = null;
  /** IANA zone for the selected company (VoIP `X-Timezone` offset); mirrored from React + storage. */
  private companyTimezoneIana: string | null = null;
  /**
   * Last auth token seen by the request interceptor. Exists for synchronous unload-time work
   * (pagehide keepalive fetch): awaiting a fresh Firebase token there is commonly cancelled by the
   * browser before the fetch is issued. At most one interceptor-cycle stale (web-dialer heartbeats
   * refresh it every 60s), well within Firebase's ~1h token validity.
   */
  private lastAuthToken: string | null = null;

  setCompanyId(id: string | null) {
    this.companyId = id;
  }

  /** Currently selected company id (null when 'global'). Used outside React, e.g. the softphone clientState. */
  getCompanyId(): string | null {
    return this.companyId;
  }

  /** Keeps VoIP date windows aligned with company TZ on native (Preferences) and before storage is read. */
  setCompanyTimezone(iana: string | null) {
    this.companyTimezoneIana = iana?.trim() || null;
  }

  setOrgId(id: string | null) {
    this.orgId = id;
  }

  getOrgId(): string | null {
    return this.orgId;
  }

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: parseInt(ENV.API.TIMEOUT.toString()),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token and company header
    this.api.interceptors.request.use(
      async (config) => {
        this.assertRequestCurrent(config);
        // Wait for Firebase to finish restoring the session before sending requests
        await authReady;
        this.assertRequestCurrent(config);
        // Get fresh token from Firebase (this handles automatic refresh)
        const token = await authService.getCurrentToken();
        this.assertRequestCurrent(config);
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
          this.lastAuthToken = token;
        }
        // Attach selected organization ID header
        // Allow per-request override: if the request already has x-org-id set, keep it
        if (!config.headers['x-org-id'] && this.orgId) {
          config.headers['x-org-id'] = this.orgId;
        }
        // Attach selected company ID header ("global" when no company selected)
        // Allow per-request override: if the request already has x-company-id set, keep it
        if (!config.headers['x-company-id']) {
          config.headers['x-company-id'] = this.companyId ?? 'global';
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Single-flight refresh promise: ensures only one token refresh runs at a time.
    // All concurrent 401 responses will await the same promise instead of each
    // triggering their own getIdToken(true) call.
    let refreshPromise: Promise<string> | null = null;

    // Response interceptor to handle auth errors
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // A locked subscription denies everything. Surface it from the denial itself so the
        // lock screen still appears when GET /api/subscription is the failing request.
        // Attributed to the organization the request was actually made for, so a late 403
        // from a previous organization cannot lock the one now selected.
        if (error.response?.status === 403 && error.response?.data?.detail === DENY_SUBSCRIPTION_LOCKED) {
          const deniedOrgId = originalRequest?.headers?.['x-org-id'] ?? this.orgId;
          if (deniedOrgId) setServerLockedOrg(String(deniedOrgId));
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
          this.assertRequestCurrent(originalRequest);
          originalRequest._retry = true;

          try {
            // Coalesce concurrent refreshes into a single Firebase call
            if (!refreshPromise) {
              refreshPromise = (async () => {
                await authReady;
                const { auth } = await import('./firebase');
                if (!auth.currentUser) throw new Error('No authenticated user');
                return auth.currentUser.getIdToken(true);
              })().finally(() => { refreshPromise = null; });
            }

            const freshToken = await refreshPromise;
            this.assertRequestCurrent(originalRequest);
            originalRequest.headers.Authorization = `Bearer ${freshToken}`;
            return this.api(originalRequest);
          } catch (refreshError) {
            clientLogger.error('Token refresh failed:', refreshError);
          }

          // Don't sign out here — Firebase's onAuthStateChanged listener already
          // handles genuine session invalidation. Signing out on a transient
          // refresh failure was causing cross-tab logouts.
        }
        return Promise.reject(error);
      }
    );
  }

  // Organizations
  async getOrganizations() {
    const response = await this.api.get('/organizations');
    return response.data;
  }

  async createOrganization(data: any) {
    const response = await this.api.post('/organizations', data);
    return response.data;
  }

  async getOrganizationById(id: string) {
    const response = await this.api.get(`/organizations/${id}`);
    return response.data;
  }

  // Users Management
  async getUsers() {
    const response = await this.api.get(`/users`);
    return response.data;
  }

  async getChatUsers(): Promise<import('@/types').ChatUserDTO[]> {
    const response = await this.api.get(`/users/chat`);
    return response.data;
  }

  async createUser(data: any) {
    const response = await this.api.post(`/users`, data);
    return response.data;
  }

  async getUserById(userId: string) {
    const response = await this.api.get(`/users/${userId}`);
    return response.data;
  }

  async updateUser(userId: string, data: any) {
    const response = await this.api.put(`/users/${userId}`, data);
    return response.data;
  }

  async deleteUser(userId: string) {
    const response = await this.api.delete(`/users/${userId}`);
    return response.data;
  }

  /** Disable a membership: revokes access and frees a seat, but keeps the user and their history. */
  async disableUser(userId: string) {
    const response = await this.api.post(`/users/${userId}/disable`);
    return response.data;
  }

  /** Re-enable a disabled membership. Fails with 409 seat_limit_reached on a capped plan. */
  async enableUser(userId: string) {
    const response = await this.api.post(`/users/${userId}/enable`);
    return response.data;
  }

  // Subscription
  /** The current organization's plan, derived state, limits and usage. */
  async getSubscription(): Promise<import('@/types').SubscriptionDTO> {
    const response = await this.api.get(`/subscription`);
    return response.data;
  }

  async changePassword(newPassword: string) {
    const response = await this.api.post(`/users/me/change-password`, { new_password: newPassword });
    return response.data;
  }

  // Extra Field Config Management - Company-specific
  async getCompanyExtraFieldConfigs() {
    const response = await this.api.get(`/companies/extrafieldconfigs`);
    return response.data;
  }

  async addCompanyExtraFieldConfig(data: any, companyId?: string) {
    const response = await this.api.post(`/companies/extrafieldconfigs`, data, {
      headers: companyId ? { 'x-company-id': companyId } : undefined,
    });
    return response.data;
  }

  async editExtraFieldConfig(id: string, data: any, companyId?: string) {
    const response = await this.api.put(`/extrafieldconfigs/${id}`, data, {
      headers: companyId ? { 'x-company-id': companyId } : undefined,
    });
    return response.data;
  }

  async deleteExtraFieldConfig(id: string, companyId?: string) {
    const response = await this.api.delete(`/extrafieldconfigs/${id}`, {
      headers: companyId ? { 'x-company-id': companyId } : undefined,
    });
    return response.data;
  }

  async getCompanyExtraFieldConfigsByTable(tableName: string, companyId?: string) {
    const response = await this.api.get(`/companies/extrafieldconfigs/table/${tableName}`, {
      headers: companyId ? { 'x-company-id': companyId } : undefined,
    });
    return response.data;
  }

  // Get extendable tables
  async getExtendableTables() {
    const response = await this.api.get('/extendable-tables');
    return response.data;
  }

  // Technicians
  async getTechnicians(companyId?: string) {
    const response = await this.api.get('/technicians', {
      headers: companyId ? { 'x-company-id': companyId } : undefined,
    });
    return response.data;
  }

  async createTechnician(data: any) {
    const response = await this.api.post('/technicians', data);
    return response.data;
  }

  async getTechnicianById(id: string) {
    const response = await this.api.get(`/technicians/${id}`);
    return response.data;
  }

  /** Total + total pending balance for a technician (admin/secretary view), scoped to the selected company. */
  async getTechnicianBalance(id: string): Promise<TechnicianBalanceDTO> {
    const response = await this.api.get(`/technicians/${id}/balance`);
    return response.data;
  }

  /** Balance totals for every accessible technician, keyed by technician id. */
  async getTechnicianBalances(): Promise<Record<string, TechnicianBalanceDTO>> {
    const response = await this.api.get('/technicians/balances');
    return response.data;
  }

  /** Drill-down: the jobs behind a balance figure. bucket = 'balance' | 'pending'. */
  async getTechnicianBalanceJobs(id: string, bucket: 'balance' | 'pending'): Promise<JobListItemDTO[]> {
    const response = await this.api.get(`/technicians/${id}/balance/jobs`, { params: { bucket } });
    return response.data;
  }

  /** The current technician's own balance totals (self-view). */
  async getMyBalance(): Promise<TechnicianBalanceDTO> {
    const response = await this.api.get(`/technicians/me/balance`);
    return response.data;
  }

  /** The current technician's own balance drill-down jobs. */
  async getMyBalanceJobs(bucket: 'balance' | 'pending'): Promise<JobListItemDTO[]> {
    const response = await this.api.get(`/technicians/me/balance/jobs`, { params: { bucket } });
    return response.data;
  }

  async attachCompaniesToTechnician(technicianId: string, companyIds: string[]) {
    const response = await this.api.post(`/technicians/${technicianId}/companies`, { company_ids: companyIds });
    return response.data;
  }

  async getCompaniesByTechnician(technicianId: string) {
    const response = await this.api.get(`/technicians/${technicianId}/companies`);
    return response.data;
  }

  async attachJobTypesToTechnician(technicianId: string, jobTypeIds: string[]) {
    const response = await this.api.post(`/technicians/${technicianId}/jobtypes`, { job_type_ids: jobTypeIds });
    return response.data;
  }

  async getJobTypesByTechnician(technicianId: string) {
    const response = await this.api.get(`/technicians/${technicianId}/jobtypes`);
    return response.data;
  }

  async detachCompaniesFromTechnician(technicianId: string, companyIds: string[]) {
    const response = await this.api.delete(`/technicians/${technicianId}/companies`, { data: { company_ids: companyIds } });
    return response.data;
  }

  async detachJobTypesFromTechnician(technicianId: string, jobTypeIds: string[]) {
    const response = await this.api.delete(`/technicians/${technicianId}/jobtypes`, { data: { job_type_ids: jobTypeIds } });
    return response.data;
  }

  // Technician User Account Management
  async createUserForTechnician(technicianId: string, data: { email?: string }) {
    const response = await this.api.post(`/technicians/${technicianId}/user`, data);
    return response.data;
  }

  async revokeUserForTechnician(technicianId: string) {
    // Calls Data service which clears UserID in the Technician table, then deletes from Management service
    const response = await this.api.delete(`/technicians/${technicianId}/user`);
    return response.data;
  }

  async deleteTechnician(technicianId: string) {
    const response = await this.api.delete(`/technicians/${technicianId}`);
    return response.data as import('@/types').Technician;
  }

  async mergePatchTechnician(id: string, data: import('@/types').TechnicianMergePatchDTO) {
    const response = await this.api.patch(`/technicians/${id}`, data, {
      headers: {
        'Content-Type': 'application/merge-patch+json',
      },
    });
    return response.data;
  }

  // Technician Attachments
  async prepareTechnicianAttachmentUpload(technicianId: string, fileName: string) {
    const response = await this.api.post(`/media/technicians/${technicianId}/prepare-upload`, { file_name: fileName });
    return response.data as import('@/types').PrepareUploadResponse;
  }

  async getTechnicianAttachments(technicianId: string) {
    const response = await this.api.get(`/media/technicians/${technicianId}`);
    return response.data as import('@/types').TechnicianAttachmentDTO[];
  }

  async createTechnicianAttachment(technicianId: string, data: import('@/types').CreateTechnicianAttachmentDTO) {
    const response = await this.api.post(`/technicians/${technicianId}/attachments`, data);
    return response.data as import('@/types').TechnicianAttachmentDTO;
  }

  async updateTechnicianAttachment(technicianId: string, attachmentId: string, data: import('@/types').UpdateTechnicianAttachmentDTO) {
    const response = await this.api.patch(`/technicians/${technicianId}/attachments/${attachmentId}`, data);
    return response.data as import('@/types').TechnicianAttachmentDTO;
  }

  async deleteTechnicianAttachment(technicianId: string, attachmentId: string) {
    const response = await this.api.delete(`/media/technicians/${technicianId}/attachments/${attachmentId}`);
    return response.data;
  }

  async getTechnicianAttachmentDownloadUrl(technicianId: string, objectKey: string) {
    const response = await this.api.post(`/media/technicians/${technicianId}/download-url`, { object_key: objectKey });
    return response.data as { download_url: string };
  }

  async attachJobTypesToCompany(jobTypeIds: string[], companyId?: string) {
    const response = await this.api.post(`/companies/jobtypes`, { job_type_ids: jobTypeIds }, {
      headers: companyId ? { 'x-company-id': companyId } : undefined,
    });
    return response.data;
  }

  async getJobTypesByCompany(companyId?: string) {
    const response = await this.api.get(`/companies/jobtypes`, {
      headers: companyId ? { 'x-company-id': companyId } : undefined,
    });
    return response.data;
  }

  async detachJobTypesFromCompany(jobTypeIds: string[], companyId?: string) {
    const response = await this.api.delete(`/companies/jobtypes`, {
      data: { job_type_ids: jobTypeIds },
      headers: companyId ? { 'x-company-id': companyId } : undefined,
    });
    return response.data;
  }

  // Organizations
  async getMyOrganizations(): Promise<import('@/types').UserOrganizationDTO[]> {
    const response = await this.api.get('/users/me/organizations');
    return response.data;
  }

  async markSetupWizardShown(): Promise<void> {
    await this.api.post('/organizations/current/setup-wizard-shown');
  }

  // Companies
  async getCompanies(companyName?: string): Promise<import('@/types').CompanyDTO[]> {
    const params = companyName ? { company_name: companyName } : {};
    const response = await this.api.get('/companies', { params });
    return response.data;
  }

  async getCompanyById(id: string) {
    const response = await this.api.get(`/companies/${id}`);
    return response.data;
  }

  async createCompany(data: any) {
    const response = await this.api.post('/companies', data);
    return response.data;
  }

  async updateCompany(companyId: string, data: any) {
    const response = await this.api.put(`/companies/${companyId}`, data);
    return response.data;
  }

  async getCompanyInvoiceSettings(companyId?: string) {
    const response = await this.api.get(`/companies/invoice-settings`, {
      headers: companyId ? { 'x-company-id': companyId } : undefined,
    });
    return response.data;
  }

  async updateCompanyInvoiceSettings(
    data: UpdateCompanyInvoiceDTO,
    companyId?: string
  ): Promise<CompanyInvoiceDTO> {
    const response = await this.api.put(`/companies/invoice-settings`, data, {
      headers: companyId ? { 'x-company-id': companyId } : undefined,
    });
    return response.data;
  }

  // Payment Method Configs
  async getPaymentMethodConfigs(companyId?: string): Promise<CompanyPaymentMethodConfigDTO[]> {
    const response = await this.api.get(`/companies/payment-methods`, {
      headers: companyId ? { 'x-company-id': companyId } : undefined,
    });
    return response.data;
  }

  async updatePaymentMethodConfigs(data: UpdateCompanyPaymentMethodConfigsDTO): Promise<CompanyPaymentMethodConfigDTO[]> {
    const response = await this.api.put(`/companies/payment-methods`, data);
    return response.data;
  }

  // Job Types
  async getJobTypes() {
    const response = await this.api.get('/jobtypes');
    return response.data;
  }

  async createJobType(data: any) {
    const response = await this.api.post('/jobtypes', data);
    return response.data;
  }

  async updateJobType(jobTypeId: string, data: UpdateJobTypeDTO) {
    const response = await this.api.put(`/jobtypes/${jobTypeId}`, data);
    return response.data;
  }

  async deleteJobType(jobTypeId: string) {
    await this.api.delete(`/jobtypes/${jobTypeId}`);
  }

  // Job Sources
  async getJobSources() {
    const response = await this.api.get('/jobsources');
    return response.data;
  }

  async createJobSource(data: any) {
    const response = await this.api.post('/jobsources', data);
    return response.data;
  }

  async getJobSourcesByCompany(companyId?: string) {
    const response = await this.api.get(`/companies/jobsources`, {
      headers: companyId ? { 'x-company-id': companyId } : undefined,
    });
    return response.data;
  }

  async updateJobSource(jobSourceId: string, data: any) {
    const response = await this.api.put(`/jobsources/${jobSourceId}`, data);
    return response.data;
  }

  async attachJobSourcesToCompany(jobSourceIds: string[], companyId?: string) {
    const response = await this.api.post(`/companies/jobsources`, { job_source_ids: jobSourceIds }, {
      headers: companyId ? { 'x-company-id': companyId } : undefined,
    });
    return response.data;
  }

  async detachJobSourcesFromCompany(jobSourceIds: string[], companyId?: string) {
    const response = await this.api.delete(`/companies/jobsources`, {
      data: { job_source_ids: jobSourceIds },
      headers: companyId ? { 'x-company-id': companyId } : undefined,
    });
    return response.data;
  }

  // Job Statuses
  async getJobStatusesByCompany(companyId?: string) {
    const response = await this.api.get(`/companies/job-statuses`, {
      headers: companyId ? { 'x-company-id': companyId } : undefined,
    });
    return response.data;
  }

  async createJobStatus(data: any, companyId?: string) {
    const response = await this.api.post(`/companies/job-statuses`, data, {
      headers: companyId ? { 'x-company-id': companyId } : undefined,
    });
    return response.data;
  }

  async updateJobStatus(statusId: string, data: any, companyId?: string) {
    const response = await this.api.put(`/companies/job-statuses/${statusId}`, data, {
      headers: companyId ? { 'x-company-id': companyId } : undefined,
    });
    return response.data;
  }

  async deleteJobStatus(statusId: string, companyId?: string) {
    const response = await this.api.delete(`/companies/job-statuses/${statusId}`, {
      headers: companyId ? { 'x-company-id': companyId } : undefined,
    });
    return response.data;
  }

  async updateJobStatusOrder(data: { job_statuses: Array<{ id: string; sort_order: number }> }, companyId?: string): Promise<void> {
    const response = await this.api.put(`/companies/job-statuses/order-update`, data, {
      headers: companyId ? { 'x-company-id': companyId } : undefined,
    });
    return response.data;
  }

  async updateDefaultJobStatuses(data: import('@/types').UpdateDefaultJobStatusesDTO, companyId?: string): Promise<{ default_status_for_new_job_id?: string; default_status_for_completed_job_id?: string }> {
    const response = await this.api.put(`/companies/default-job-statuses`, data, {
      headers: companyId ? { 'x-company-id': companyId } : undefined,
    });
    return response.data;
  }

  // Tags - Company scoped
  async getTags(companyId?: string): Promise<import('@/types').TagDTO[]> {
    const response = await this.api.get(`/companies/tags`, {
      headers: companyId ? { 'x-company-id': companyId } : undefined,
    });
    return response.data;
  }

  async createTag(data: import('@/types').CreateTagDTO, companyId?: string): Promise<import('@/types').TagDTO> {
    const response = await this.api.post(`/companies/tags`, data, {
      headers: companyId ? { 'x-company-id': companyId } : undefined,
    });
    return response.data;
  }

  async updateTag(tagId: string, data: import('@/types').UpdateTagDTO, companyId?: string): Promise<import('@/types').TagDTO> {
    const response = await this.api.patch(`/companies/tags/${tagId}`, data, {
      headers: companyId ? { 'x-company-id': companyId } : undefined,
    });
    return response.data;
  }

  async deleteTag(tagId: string, companyId?: string): Promise<void> {
    await this.api.delete(`/companies/tags/${tagId}`, {
      headers: companyId ? { 'x-company-id': companyId } : undefined,
    });
  }

  // Lead tags - separate from job tags
  async getLeadTags(companyId?: string, includeArchived = false): Promise<import('@/types').LeadTagDTO[]> {
    const response = await this.api.get('/companies/lead-tags', {
      params: { includeArchived },
      headers: companyId ? { 'x-company-id': companyId } : undefined,
    });
    return response.data;
  }

  async createLeadTag(data: { name: string; color?: string }, companyId?: string): Promise<import('@/types').LeadTagDTO> {
    const response = await this.api.post('/companies/lead-tags', data, { headers: companyId ? { 'x-company-id': companyId } : undefined });
    return response.data;
  }

  async updateLeadTag(id: string, data: { name?: string; color?: string }, companyId?: string): Promise<import('@/types').LeadTagDTO> {
    const response = await this.api.patch(`/companies/lead-tags/${id}`, data, { headers: companyId ? { 'x-company-id': companyId } : undefined });
    return response.data;
  }

  async archiveLeadTag(id: string, companyId?: string): Promise<void> {
    await this.api.delete(`/companies/lead-tags/${id}`, { headers: companyId ? { 'x-company-id': companyId } : undefined });
  }

  async restoreLeadTag(id: string, companyId?: string): Promise<import('@/types').LeadTagDTO> {
    const response = await this.api.post(`/companies/lead-tags/${id}/restore`, {}, { headers: companyId ? { 'x-company-id': companyId } : undefined });
    return response.data;
  }

  // Catalog Items - Company scoped
  async getCatalogItems(companyId?: string): Promise<import('@/types').CatalogItemDTO[]> {
    const response = await this.api.get(`/companies/catalog-items`, {
      headers: companyId ? { 'x-company-id': companyId } : undefined,
    });
    return response.data;
  }

  async searchCatalogItems(data: import('@/types').CatalogItemSearchDTO, companyId?: string): Promise<import('@/types').CatalogItemSearchResultDTO> {
    const response = await this.api.post(`/companies/catalog-items/search`, data, {
      headers: companyId ? { 'x-company-id': companyId } : undefined,
    });
    return response.data;
  }

  async createCatalogItem(data: import('@/types').CreateCatalogItemDTO, companyId?: string): Promise<import('@/types').CatalogItemDTO> {
    const response = await this.api.post(`/companies/catalog-items`, data, {
      headers: companyId ? { 'x-company-id': companyId } : undefined,
    });
    return response.data;
  }

  async updateCatalogItem(itemId: string, data: import('@/types').UpdateCatalogItemDTO, companyId?: string): Promise<import('@/types').CatalogItemDTO> {
    const response = await this.api.patch(`/companies/catalog-items/${itemId}`, data, {
      headers: companyId ? { 'x-company-id': companyId } : undefined,
    });
    return response.data;
  }

  async deleteCatalogItem(itemId: string, companyId?: string): Promise<void> {
    await this.api.delete(`/companies/catalog-items/${itemId}`, {
      headers: companyId ? { 'x-company-id': companyId } : undefined,
    });
  }

  async prepareCatalogItemImageUpload(itemId: string, fileName: string): Promise<import('@/types').PrepareUploadResponse> {
    const response = await this.api.post(`/media/catalog-items/${itemId}/prepare-upload`, { file_name: fileName });
    return response.data;
  }

  async resolveDownloadUrls(objectKeys: string[]): Promise<Record<string, string>> {
    const response = await this.api.post(`/media/resolve-urls`, { object_keys: objectKeys });
    return response.data.urls;
  }

  async deleteCatalogItemImage(itemId: string, objectKey: string): Promise<void> {
    await this.api.delete(`/media/catalog-items/${itemId}/image`, { data: { object_key: objectKey } });
  }

  // Company Logo
  async prepareCompanyLogoUpload(companyId: string, fileName: string): Promise<import('@/types').PrepareUploadResponse> {
    const response = await this.api.post(`/media/companies/${companyId}/prepare-logo-upload`, { file_name: fileName });
    return response.data;
  }

  async deleteCompanyLogo(companyId: string, objectKey: string): Promise<void> {
    await this.api.delete(`/media/companies/${companyId}/logo`, { data: { object_key: objectKey } });
  }

  // Job Tags
  async getJobTags(jobId: string): Promise<import('@/types').TagDTO[]> {
    const response = await this.api.get(`/jobs/${jobId}/tags`);
    return response.data;
  }

  async setJobTags(jobId: string, tags: import('@/types').JobTagInputDTO[]): Promise<import('@/types').TagDTO[]> {
    const response = await this.api.put(`/jobs/${jobId}/tags`, { tags });
    return response.data;
  }

  async addJobTags(jobId: string, tags: import('@/types').JobTagInputDTO[]): Promise<import('@/types').TagDTO[]> {
    const response = await this.api.post(`/jobs/${jobId}/tags`, tags);
    return response.data;
  }

  async removeJobTag(jobId: string, tagId: string): Promise<void> {
    await this.api.delete(`/jobs/${jobId}/tags/${tagId}`);
  }

  // Jobs
  async getJobs() {
    const response = await this.api.get('/jobs');
    return response.data;
  }

  // Leads
  // Company-wide expenses (not job expenses). Technicians only get their own back.
  async getExpenses(params: import('@/types').ExpenseListParams): Promise<import('@/types').ExpenseListDTO> {
    const response = await this.api.get('/expenses', { params });
    return response.data;
  }

  /** Jobs with parts expenses in the date range; only from/to/page/page_size apply. */
  async getJobExpenses(params: Pick<import('@/types').ExpenseListParams, 'from' | 'to' | 'technician_id' | 'page' | 'page_size'>): Promise<import('@/types').JobExpenseListDTO> {
    const response = await this.api.get('/expenses/jobs', { params });
    return response.data;
  }

  /** companyId: the company the expense belongs to (required; the global view has none selected). */
  async createExpense(companyId: string, data: import('@/types').SaveExpenseDTO): Promise<import('@/types').ExpenseDTO> {
    const response = await this.api.post('/expenses', data, { headers: { 'x-company-id': companyId } });
    return response.data;
  }

  async updateExpense(id: string, data: import('@/types').SaveExpenseDTO): Promise<import('@/types').ExpenseDTO> {
    const response = await this.api.put(`/expenses/${id}`, data);
    return response.data;
  }

  async deleteExpense(id: string) {
    await this.api.delete(`/expenses/${id}`);
  }

  /** companyId: only categories assigned to that company (the expense form). */
  async getExpenseCategories(options: { companyId?: string; includeArchived?: boolean } = {}): Promise<import('@/types').ExpenseCategoryDTO[]> {
    const response = await this.api.get('/expense-categories', {
      params: { company_id: options.companyId, include_archived: options.includeArchived || undefined },
    });
    return response.data;
  }

  async createExpenseCategory(data: import('@/types').SaveExpenseCategoryDTO): Promise<import('@/types').ExpenseCategoryDTO> {
    const response = await this.api.post('/expense-categories', data);
    return response.data;
  }

  async updateExpenseCategory(id: string, data: import('@/types').SaveExpenseCategoryDTO): Promise<import('@/types').ExpenseCategoryDTO> {
    const response = await this.api.put(`/expense-categories/${id}`, data);
    return response.data;
  }

  /** archived: true when expenses use it, so it was archived instead of removed. */
  async deleteExpenseCategory(id: string): Promise<{ archived: boolean }> {
    const response = await this.api.delete(`/expense-categories/${id}`);
    return response.data;
  }

  async searchLeads(data: import('@/types').LeadSearchDTO): Promise<import('@/types').LeadSearchResultDTO> {
    const response = await this.api.post('/leads/search', data, { headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' } });
    return response.data;
  }

  async getLead(id: string): Promise<import('@/types').LeadDTO> {
    const response = await this.api.get(`/leads/${id}`);
    return response.data;
  }

  async getLeadContactOptions(search: string, companyId: string, page = 1): Promise<{ data: import('@/types').ContactDTO[]; total: number; page: number; pageSize: number }> {
    const response = await this.api.get('/leads/contact-options', {
      params: { search, page, page_size: 25 }, headers: { 'x-company-id': companyId },
    });
    const contacts = response.data.contacts ?? [];
    return { data: contacts, total: response.data.total_count ?? contacts.length, page: response.data.page ?? page, pageSize: response.data.page_size ?? 25 };
  }

  async getLeadFormOptions(companyId: string): Promise<import('@/types').LeadFormOptionsDTO> {
    const response = await this.api.get('/leads/form-options', { headers: { 'x-company-id': companyId } });
    return response.data;
  }

  async createLead(data: import('@/types').CreateLeadDTO): Promise<import('@/types').LeadDTO> {
    const response = await this.api.post('/leads', data);
    return response.data;
  }

  async patchLead(id: string, data: import('@/types').PatchLeadDTO): Promise<import('@/types').LeadDTO> {
    const response = await this.api.patch(`/leads/${id}`, data, { headers: { 'Content-Type': 'application/merge-patch+json' } });
    return response.data;
  }

  async archiveLead(id: string): Promise<import('@/types').LeadDTO> {
    const response = await this.api.post(`/leads/${id}/archive`);
    return response.data;
  }

  async restoreLead(id: string): Promise<import('@/types').LeadDTO> {
    const response = await this.api.post(`/leads/${id}/restore`);
    return response.data;
  }

  async deleteLead(id: string): Promise<void> {
    await this.api.delete(`/leads/${id}`);
  }

  async acceptLead(id: string): Promise<import('@/types').AcceptLeadResultDTO> {
    const response = await this.api.post(`/leads/${id}/convert-to-job`);
    return response.data;
  }

  async setLeadTags(id: string, tags: import('@/types').LeadTagInputDTO[]): Promise<import('@/types').LeadTagDTO[]> {
    const response = await this.api.put(`/leads/${id}/tags`, { tags });
    return response.data;
  }

  /** Prefill values for the new-job form from a pasted job message. Saves nothing. */
  async analyzeJobText(text: string, nowLocal: string, companyId: string): Promise<JobTextAnalysisDTO> {
    const response = await this.api.post('/jobs/analyze-text', { text, now_local: nowLocal }, {
      headers: { 'x-company-id': companyId },
    });
    return response.data;
  }

  async createJob(data: any) {
    const response = await this.api.post('/jobs', data);
    return response.data;
  }

  async getJobById(id: string) {
    const response = await this.api.get(`/jobs/${id}`);
    return response.data;
  }

  async mergePatchJob(id: string, data: JobMergePatchDTO) {
    const response = await this.api.patch(`/jobs/${id}`, data, {
      headers: {
        'Content-Type': 'application/merge-patch+json',
      },
    });
    return response.data;
  }

  async updateJobEntityStatus(id: string, data: { status_id: string }): Promise<JobStatusDTO> {
    const response = await this.api.patch(`/jobs/${id}/status`, data);
    return response.data;
  }

  async approveJob(id: string): Promise<{ approved: boolean }> {
    const response = await this.api.patch(`/jobs/${id}/approve`);
    return response.data;
  }

  // ===== Technician balance settlement =====

  /** Mark a job's technician balance as settled (completed, non-canceled jobs only). Returns the updated job. */
  async settleJobBalance(id: string): Promise<JobDTO> {
    const response = await this.api.post(`/jobs/${id}/settle-balance`);
    return response.data;
  }

  /** Clear a job's settled state (reversible). Returns the updated job. */
  async unsettleJobBalance(id: string): Promise<JobDTO> {
    const response = await this.api.post(`/jobs/${id}/unsettle-balance`);
    return response.data;
  }

  /** Batch settle/unsettle jobs (drives "settle report"). Only eligible jobs are affected; others are reported as skipped. */
  async settleBalanceBatch(jobIds: string[], settled: boolean): Promise<BatchSettleResponseDTO> {
    const response = await this.api.post(`/jobs/settle-balance`, { job_ids: jobIds, settled });
    return response.data;
  }

  async updateJobPaymentStatus(id: string, data: { payment_status: string }): Promise<{ payment_status: string }> {
    const response = await this.api.patch(`/jobs/${id}/payment-status`, data);
    return response.data;
  }

  async deleteJob(id: string) {
    const response = await this.api.delete(`/jobs/${id}`);
    return response.data;
  }

  async cancelJob(id: string, reason?: string): Promise<import('@/types').JobDTO> {
    const response = await this.api.post(`/jobs/${id}/cancel`, { reason });
    return response.data;
  }

  async uncancelJob(id: string): Promise<import('@/types').JobDTO> {
    const response = await this.api.post(`/jobs/${id}/uncancel`);
    return response.data;
  }

  // ── Job Visits ──────────────────────────────────────────────────

  async getJobVisits(jobId: string): Promise<import('@/types').JobVisitDTO[]> {
    const response = await this.api.get(`/jobs/${jobId}/visits`);
    return response.data;
  }

  async checkInToJob(jobId: string, data?: import('@/types').CheckInJobVisitDTO): Promise<import('@/types').JobVisitDTO> {
    const response = await this.api.post(`/jobs/${jobId}/visits/check-in`, data ?? {});
    return response.data;
  }

  async checkOutFromJob(jobId: string, data?: import('@/types').CheckOutJobVisitDTO): Promise<import('@/types').JobVisitDTO> {
    const response = await this.api.post(`/jobs/${jobId}/visits/check-out`, data ?? {});
    return response.data;
  }

  async createJobVisit(jobId: string, data: import('@/types').CreateJobVisitDTO): Promise<import('@/types').JobVisitDTO> {
    const response = await this.api.post(`/jobs/${jobId}/visits`, data);
    return response.data;
  }

  async updateJobVisit(jobId: string, visitId: string, data: import('@/types').PatchJobVisitDTO): Promise<import('@/types').JobVisitDTO> {
    const response = await this.api.patch(`/jobs/${jobId}/visits/${visitId}`, data);
    return response.data;
  }

  async deleteJobVisit(jobId: string, visitId: string): Promise<void> {
    await this.api.delete(`/jobs/${jobId}/visits/${visitId}`);
  }

  async getTechnicianWorkTime(technicianId: string, from: string, to: string): Promise<import('@/types').TechnicianWorkTimeDTO> {
    const response = await this.api.get(`/technicians/${technicianId}/work-time`, { params: { from, to } });
    return response.data;
  }

  /** The current technician's own work-time summary (self-view). */
  async getMyWorkTime(from: string, to: string): Promise<import('@/types').TechnicianWorkTimeDTO> {
    const response = await this.api.get(`/technicians/me/work-time`, { params: { from, to } });
    return response.data;
  }

  /** The job the current technician is checked in to right now, or null (204). */
  async getMyActiveVisit(): Promise<import('@/types').JobVisitDTO | null> {
    const response = await this.api.get(`/technicians/me/active-visit`);
    return response.status === 204 ? null : response.data;
  }

  // ── Contacts ─────────────────────────────────────────────────────

  async searchJobs(data: import('@/types').JobSearchDTO): Promise<import('@/types').JobSearchResultDTO> {
    const response = await this.api.post('/jobs/search', data, {
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache',
      },
    });
    return response.data;
  }

  // Contacts
  async getContacts(params?: { search?: string; page?: number; pageSize?: number }) {
    const response = await this.api.get('/contacts', { params });
    return response.data;
  }

  async createContact(data: any) {
    const response = await this.api.post('/contacts', data);
    return response.data;
  }

  async getContactById(contactId: string): Promise<ContactDTO> {
    const response = await this.api.get(`/contacts/${contactId}`);
    return response.data;
  }

  async updateContact(contactId: string, data: any) {
    const response = await this.api.put(`/contacts/${contactId}`, data);
    return response.data;
  }

  async deleteContact(contactId: string) {
    const response = await this.api.delete(`/contacts/${contactId}`);
    return response.data;
  }

  async lookupContactByPhone(phone: string): Promise<ContactLookupResultDTO[]> {
    const response = await this.api.get('/contacts/lookup', { params: { phone } });
    return response.status === 204 ? [] : (response.data as ContactLookupResultDTO[]);
  }

  async lookupContactByEmail(email: string): Promise<ContactLookupResultDTO[]> {
    const response = await this.api.get('/contacts/lookup', { params: { email } });
    return response.status === 204 ? [] : (response.data as ContactLookupResultDTO[]);
  }

  /** A job's contact with only the past jobs the caller may see from that job — phones masked like the job's. */
  async getJobContact(jobId: string, contactId: string): Promise<ContactDTO> {
    const response = await this.api.get(`/jobs/${jobId}/contacts/${contactId}`);
    return response.data;
  }

  async lookupContactById(contactId: string): Promise<ContactLookupResultDTO | null> {
    const response = await this.api.get('/contacts/lookup', { params: { contactId } });
    return response.status === 204 ? null : (response.data as ContactLookupResultDTO);
  }

  async getContactJobCount(contactId: string): Promise<import('@/types').ContactJobCountDTO> {
    const response = await this.api.get(`/contacts/${contactId}/job-count`);
    return response.data;
  }

  async attachContactToJob(jobId: string, data: any) {
    const response = await this.api.post(`/jobs/${jobId}/contacts`, data);
    return response.data;
  }

  async updateJobContact(jobId: string, contactId: string, data: any) {
    const response = await this.api.put(`/jobs/${jobId}/contacts/${contactId}`, data);
    return response.data;
  }

  async detachContactFromJob(jobId: string, contactId: string) {
    const response = await this.api.delete(`/jobs/${jobId}/contacts/${contactId}`);
    return response.data;
  }

  async getSearchLookup() {
    const response = await this.api.get('/search-lookup');
    return response.data;
  }

  // Invoices
  async getInvoicesByCompany() {
    const response = await this.api.get('/Invoices/all');
    return response.data;
  }

  async searchInvoices(data: import('@/types').InvoiceSearchDTO): Promise<import('@/types').InvoiceSearchResultDTO> {
    const response = await this.api.post('/Invoices/search', data);
    return response.data;
  }

  async getInvoiceSummary(): Promise<import('@/types').InvoiceSummaryDTO> {
    const response = await this.api.get('/Invoices/summary');
    return response.data;
  }

  async getInvoiceById(invoiceId: string) {
    const response = await this.api.get(`/Invoices/${invoiceId}`);
    return response.data;
  }

  async getInvoiceByJobId(jobId: string) {
    const response = await this.api.get(`/Invoices`, { params: { jobId: jobId } });
    return response.data;
  }

  async createInvoice(data: CreateInvoiceDto) {
    const response = await this.api.post('/Invoices', data);
    return response.data;
  }

  async patchInvoice(invoiceId: string, data: import('@/types').InvoicePatchDTO) {
    const response = await this.api.patch(`/Invoices/${invoiceId}`, data, {
      headers: {
        'Content-Type': 'application/merge-patch+json',
      },
    });
    return response.data;
  }

  /**
   * Ensures an invoice exists for the given job. Returns existing invoice if one exists,
   * otherwise creates a new invoice from the job data.
   * Requires the job to have an invoice number set.
   */
  async ensureInvoiceExists(jobId: string) {
    const response = await this.api.post(`/Invoices/ensure/${jobId}`);
    return response.data;
  }

  async getInvoicePdfUrl(jobId?: string, invoiceId?: string, invoiceGuid?: string): Promise<{ url: string; expires_in: string }> {
    // If we have an invoice ID, use the new endpoint that handles regeneration
    if (invoiceId) {
      const response = await this.api.get(`/Invoices/${invoiceId}/pdf-url`);
      return response.data;
    }

    const params: any = {};
    if (jobId) {
      params['job-id'] = jobId;
    }
    if (invoiceGuid) {
      params['invoice-guid'] = invoiceGuid;
    }

    const response = await this.api.get('/invoicefiles/get-pdf', {
      params
    });
    return response.data;
  }

  async searchInvoiceItems(search: string): Promise<InvoiceItemAutocompleteDto[]> {
    const response = await this.api.get('/Invoices/items/autocomplete', {
      params: { search }
    });
    return response.data;
  }

  async getInvoiceItemById(itemId: string): Promise<InvoiceItemFullDto> {
    const response = await this.api.get(`/Invoices/items/${itemId}`);
    return response.data;
  }

  // Invoice Token Management
  async createInvoiceToken(invoiceId: string): Promise<InvoicePortalTokenResponse> {
    const response = await this.api.post(`/invoices/${invoiceId}/public-token`);
    return response.data;
  }

  async revokeInvoiceToken(invoiceId: string) {
    const response = await this.api.post(`/invoices/${invoiceId}/revoke-token`);
    return response.data;
  }

  /**
   * Submit a signature for an invoice (authenticated endpoint for technicians).
   * Also marks the invoice as viewed.
   */
  async submitInvoiceSignature(invoiceId: string, signaturePath: string, signedByName: string): Promise<void> {
    await this.api.post(`/invoices/${invoiceId}/signature`, { signature_path: signaturePath, signed_by_name: signedByName });
  }

  async markInvoiceSent(invoiceId: string): Promise<void> {
    await this.api.post(`/invoices/${invoiceId}/mark-sent`);
  }

  // Estimate Token Management
  async createEstimateToken(estimateId: string): Promise<EstimatePortalTokenResponse> {
    const response = await this.api.post(`/estimates/${estimateId}/public-token`);
    return response.data;
  }

  async revokeEstimateToken(estimateId: string) {
    const response = await this.api.post(`/estimates/${estimateId}/revoke-token`);
    return response.data;
  }

  async submitEstimateSignature(estimateId: string, signaturePath: string, signedByName: string): Promise<void> {
    await this.api.post(`/estimates/${estimateId}/signature`, { signature_path: signaturePath, signed_by_name: signedByName });
  }

  // Token management
  // These methods now use cross-platform storage that works on both native (Capacitor) and web
  async setToken(token: string) {
    // Store token using cross-platform storage
    // Uses Capacitor Preferences on native, localStorage on web
    const { setStorageItem } = await import('./capacitor');
    await setStorageItem('jwt_token', token);
  }

  async getToken(): Promise<string | null> {
    // Return cached token from cross-platform storage
    // Uses Capacitor Preferences on native, localStorage on web
    const { getStorageItem } = await import('./capacitor');
    return await getStorageItem('jwt_token');
  }

  async clearToken() {
    const { removeStorageItem } = await import('./capacitor');
    await removeStorageItem('jwt_token');
  }

  // Media/Attachment Upload Methods
  async prepareAttachmentUpload(jobId: string, attachmentCount: number, attachmentFileNames?: string[]) {
    const response = await this.api.post(`/media/jobs/${jobId}/prepare-upload`, { attachment_count: attachmentCount, attachment_file_names: attachmentFileNames });
    return response.data as PrepareUploadResponse[];
  }

  async completeAttachmentUpload(jobId: string, uploads: CompleteUploadItem[]) {
    const response = await this.api.post(`/media/jobs/${jobId}/complete-upload`, uploads);
    return response.data;
  }

  async getJobAttachments(jobId: string) {
    const response = await this.api.get(`/media/jobs/${jobId}`);
    return response.data as JobAttachmentInfo[];
  }

  async getAttachmentDownloadUrl(jobId: string, attachmentId: string) {
    const response = await this.api.get(`/media/jobs/${jobId}/attachments/${attachmentId}/download`);
    return response.data as { download_url: string };
  }

  async updateJobAttachment(jobId: string, attachmentId: string, data: AttachmentUpdate) {
    const response = await this.api.patch(`/media/jobs/${jobId}/attachments/${attachmentId}`, data);
    return response.data as JobAttachmentInfo;
  }

  async deleteJobAttachment(jobId: string, attachmentId: string) {
    const response = await this.api.delete(`/media/jobs/${jobId}/attachments/${attachmentId}`);
    return response.data;
  }

  // Estimate attachments: same Media endpoints as jobs, under /media/estimates
  async prepareEstimateAttachmentUpload(estimateId: string, attachmentCount: number, attachmentFileNames?: string[]) {
    const response = await this.api.post(`/media/estimates/${estimateId}/prepare-upload`, { attachment_count: attachmentCount, attachment_file_names: attachmentFileNames });
    return response.data as PrepareUploadResponse[];
  }

  async completeEstimateAttachmentUpload(estimateId: string, uploads: CompleteUploadItem[]) {
    const response = await this.api.post(`/media/estimates/${estimateId}/complete-upload`, uploads);
    return response.data;
  }

  async getEstimateAttachments(estimateId: string) {
    const response = await this.api.get(`/media/estimates/${estimateId}`);
    return response.data as JobAttachmentInfo[];
  }

  async getEstimateAttachmentDownloadUrl(estimateId: string, attachmentId: string) {
    const response = await this.api.get(`/media/estimates/${estimateId}/attachments/${attachmentId}/download`);
    return response.data as { download_url: string };
  }

  async updateEstimateAttachment(estimateId: string, attachmentId: string, data: AttachmentUpdate) {
    const response = await this.api.patch(`/media/estimates/${estimateId}/attachments/${attachmentId}`, data);
    return response.data as JobAttachmentInfo;
  }

  async deleteEstimateAttachment(estimateId: string, attachmentId: string) {
    const response = await this.api.delete(`/media/estimates/${estimateId}/attachments/${attachmentId}`);
    return response.data;
  }

  // Company expense receipts: same Media endpoints as jobs, under /media/expenses
  async prepareCompanyExpenseAttachmentUpload(expenseId: string, attachmentCount: number, attachmentFileNames?: string[]) {
    const response = await this.api.post(`/media/expenses/${expenseId}/prepare-upload`, { attachment_count: attachmentCount, attachment_file_names: attachmentFileNames });
    return response.data as PrepareUploadResponse[];
  }

  async completeCompanyExpenseAttachmentUpload(expenseId: string, uploads: CompleteUploadItem[]) {
    const response = await this.api.post(`/media/expenses/${expenseId}/complete-upload`, uploads);
    return response.data;
  }

  async getCompanyExpenseAttachments(expenseId: string) {
    const response = await this.api.get(`/media/expenses/${expenseId}`);
    return response.data as JobAttachmentInfo[];
  }

  async deleteCompanyExpenseAttachment(expenseId: string, attachmentId: string) {
    const response = await this.api.delete(`/media/expenses/${expenseId}/attachments/${attachmentId}`);
    return response.data;
  }

  // Expense Attachment Methods
  async prepareExpenseAttachmentUpload(jobId: string, attachmentCount: number, attachmentFileNames?: string[]) {
    const response = await this.api.post(`/media/jobs/${jobId}/expense-attachments/prepare-upload`, { attachment_count: attachmentCount, attachment_file_names: attachmentFileNames });
    return response.data as PrepareUploadResponse[];
  }

  async completeExpenseAttachmentUpload(jobId: string, uploads: CompleteExpenseUploadItem[]) {
    const response = await this.api.post(`/media/jobs/${jobId}/expense-attachments/complete-upload`, uploads);
    return response.data;
  }

  async getJobExpenseAttachments(jobId: string) {
    const response = await this.api.get(`/media/jobs/${jobId}/expense-attachments`);
    return response.data as ExpenseAttachmentInfo[];
  }

  async getExpenseAttachmentDownloadUrl(jobId: string, attachmentId: string) {
    const response = await this.api.get(`/media/jobs/${jobId}/expense-attachments/${attachmentId}/download`);
    return response.data as { download_url: string };
  }

  async updateJobExpenseAttachment(jobId: string, attachmentId: string, data: { title: string }) {
    const response = await this.api.patch(`/media/jobs/${jobId}/expense-attachments/${attachmentId}`, data);
    return response.data as ExpenseAttachmentInfo;
  }

  async deleteJobExpenseAttachment(jobId: string, attachmentId: string) {
    const response = await this.api.delete(`/media/jobs/${jobId}/expense-attachments/${attachmentId}`);
    return response.data;
  }

  // Email/Gmail Integration Methods
  async getEmailAuthUrl(): Promise<{ auth_url: string }> {
    const response = await this.api.get('/email/auth/url');
    return response.data;
  }

  async getEmailStatus(): Promise<{ is_connected: boolean; email_address?: string; service_strike?: { is_configured: boolean; sender_name?: string; reply_to?: string; available_from_addresses: { context: string; address: string }[] } }> {
    const response = await this.api.get('/email/status');
    return response.data;
  }

  async getEmailAccessToken(): Promise<{ access_token: string; expires_in_seconds: number }> {
    const response = await this.api.get('/email/access-token');
    return response.data;
  }

  async disconnectEmail(): Promise<{ message: string }> {
    const response = await this.api.post('/email/disconnect');
    return response.data;
  }

  async sendEmail(request: {
    to: string;
    subject: string;
    body: string;
    is_html?: boolean;
    sender_type?: 'gmail' | 'service_strike';
    email_context?: string;
    attachments?: { filename: string; mime_type: string; data: string }[];
    attachment_refs?: { type: string; resource_id: string }[];
    template?: { name: string; button_url: string; button_label: string };
  }): Promise<{ message_id: string }> {
    const response = await this.api.post('/email/send', request);
    return response.data;
  }

  // Service Strike Email methods
  async getServiceStrikeEmailStatus(): Promise<{ is_configured: boolean; sender_name?: string; reply_to?: string; available_from_addresses: { context: string; address: string }[] }> {
    const response = await this.api.get('/email/service-strike/status');
    return response.data;
  }

  async configureServiceStrikeEmail(data: { sender_name?: string; reply_to_email?: string }): Promise<{ message: string }> {
    const response = await this.api.post('/email/service-strike/configure', data);
    return response.data;
  }

  // Email defaults (per-company default sending method, split by audience)
  async getEmailDefaults(): Promise<{
    customer_method: 'gmail' | 'service_strike' | null;
    technician_method: 'gmail' | 'service_strike' | null;
    gmail_connected: boolean;
    service_strike_configured: boolean;
  }> {
    const response = await this.api.get('/email/defaults');
    return response.data;
  }

  async configureEmailDefaults(data: {
    customer_method: 'gmail' | 'service_strike' | null;
    technician_method: 'gmail' | 'service_strike' | null;
  }): Promise<{ message: string }> {
    const response = await this.api.post('/email/defaults', data);
    return response.data;
  }

  // Per-flow email templates ("invoice" | "estimate" | "report")
  async getEmailTemplate(flow: string): Promise<EmailTemplateConfig> {
    const response = await this.api.get(`/email/templates/${flow}`);
    return response.data;
  }

  async getEmailTemplates(): Promise<EmailTemplateConfig[]> {
    const response = await this.api.get('/email/templates');
    return response.data;
  }

  async saveEmailTemplate(flow: string, data: { subject: string; body: string; button_label?: string | null }): Promise<{ message: string }> {
    const response = await this.api.post(`/email/templates/${flow}`, data);
    return response.data;
  }

  async resetEmailTemplate(flow: string): Promise<{ message: string }> {
    const response = await this.api.delete(`/email/templates/${flow}`);
    return response.data;
  }

  // Per-flow SMS templates ("appointment_confirmation" | "appointment_reminder" |
  // "review_request" | "technician_on_the_way")
  async getSmsTemplates(): Promise<SmsTemplateConfig[]> {
    const response = await this.api.get('/voip/sms-templates');
    return response.data;
  }

  async getSmsTemplate(flow: string): Promise<SmsTemplateConfig> {
    const response = await this.api.get(`/voip/sms-templates/${flow}`);
    return response.data;
  }

  async saveSmsTemplate(flow: string, data: { body: string }): Promise<{ success: boolean }> {
    const response = await this.api.post(`/voip/sms-templates/${flow}`, data);
    return response.data;
  }

  async resetSmsTemplate(flow: string): Promise<{ success: boolean }> {
    const response = await this.api.delete(`/voip/sms-templates/${flow}`);
    return response.data;
  }

  // ===== Compensation & Profit API =====
  // All financial APIs now use the /financials prefix for clear service separation

  /** Calculate technician compensation for a job */
  async calculateTechnicianCut(jobId: string): Promise<CompensationResultDTO> {
    const response = await this.api.post(`/financials/jobs/${jobId}/compensation/calculate`);
    return response.data;
  }

  /** Apply a manual override to technician compensation */
  async overrideTechnicianCut(jobId: string, data: OverrideCompensationDTO): Promise<CompensationResultDTO> {
    const response = await this.api.post(`/financials/jobs/${jobId}/compensation/override`, data);
    return response.data;
  }

  /** Remove override and recalculate compensation */
  async removeCompensationOverride(jobId: string): Promise<CompensationResultDTO> {
    const response = await this.api.delete(`/financials/jobs/${jobId}/compensation/override`);
    return response.data;
  }

  /** Calculate job profit (requires technician cut to be finalized) */
  async calculateJobProfit(jobId: string): Promise<ProfitResultDTO> {
    const response = await this.api.post(`/financials/jobs/${jobId}/profit/calculate`);
    return response.data;
  }

  /** Invalidate (clear) compensation data for a job */
  async invalidateCompensation(jobId: string): Promise<void> {
    await this.api.delete(`/financials/jobs/${jobId}/compensation`);
  }

  /** Get the complete financial summary for a job */
  async getJobFinancialSummary(jobId: string): Promise<JobFinancialSummaryDTO> {
    const response = await this.api.get(`/jobs/${jobId}/financial-summary`);
    return response.data;
  }

  // ===== Technician Compensation Plan API =====
  // All plan APIs now use the /financials prefix for clear service separation

  /** Get all technician compensation plans for a company */
  async getTechnicianPlans(): Promise<TechnicianCompensationPlan[]> {
    const response = await this.api.get(`/financials/technician-plans`);
    return response.data;
  }

  /** Get a specific technician's compensation plan */
  async getTechnicianPlan(technicianId: string): Promise<TechnicianCompensationPlan> {
    const response = await this.api.get(`/financials/technician-plans/${technicianId}`);
    return response.data;
  }

  /** Create a new technician compensation plan */
  async createTechnicianPlan(data: CreateTechnicianPlanDTO, companyId: string): Promise<TechnicianCompensationPlan> {
    const response = await this.api.post(`/financials/technician-plans`, data, { headers: { 'x-company-id': companyId } });
    return response.data;
  }

  /** Update a technician compensation plan */
  async updateTechnicianPlan(technicianId: string, data: UpdateTechnicianPlanDTO, companyId: string): Promise<TechnicianCompensationPlan> {
    const response = await this.api.put(`/financials/technician-plans/${technicianId}`, data, { headers: { 'x-company-id': companyId } });
    return response.data;
  }

  /** Delete a technician compensation plan */
  async deleteTechnicianPlan(technicianId: string, companyId: string): Promise<void> {
    await this.api.delete(`/financials/technician-plans/${technicianId}`, { headers: { 'x-company-id': companyId } });
  }

  /** Get all compensation plans for an technician (across all companies) */
  async getTechnicianCompensationPlans(technicianId: string): Promise<TechnicianCompensationPlan[]> {
    const response = await this.api.get(`/financials/technicians/${technicianId}/compensation-plans`);
    return response.data;
  }

  // ===== Reports API =====

  /** Run a new report */
  async runReport(request: import('@/types').RunReportRequestDTO): Promise<import('@/types').ReportRunDTO> {
    const response = await this.api.post('/reports/run', request);
    return response.data;
  }

  /** Get all report runs for the current user */
  async getReports(): Promise<import('@/types').ReportRunDTO[]> {
    const response = await this.api.get('/reports');
    return response.data;
  }

  /** Get a specific report by ID */
  async getReportById(reportId: string): Promise<import('@/types').ReportRunDetailDTO> {
    const response = await this.api.get(`/reports/${reportId}`);
    return response.data;
  }

  /** Delete a report by ID */
  async deleteReport(reportId: string): Promise<void> {
    await this.api.delete(`/reports/${reportId}`);
  }

  // ===== Report Templates API =====

  /** Get all templates for a company (id + name only) */
  async getReportTemplates(): Promise<import('@/types').ReportTemplateSummaryDTO[]> {
    const response = await this.api.get('/reports/templates');
    return response.data;
  }

  /** Get a specific template by ID */
  async getReportTemplateById(templateId: string): Promise<import('@/types').ReportTemplateDTO> {
    const response = await this.api.get(`/reports/templates/${templateId}`);
    return response.data;
  }

  /** Create a new template */
  async createReportTemplate(data: import('@/types').CreateReportTemplateDTO): Promise<import('@/types').ReportTemplateDTO> {
    const response = await this.api.post('/reports/templates', data);
    return response.data;
  }

  /** Update a template */
  async updateReportTemplate(templateId: string, data: import('@/types').CreateReportTemplateDTO): Promise<import('@/types').ReportTemplateDTO> {
    const response = await this.api.put(`/reports/templates/${templateId}`, data);
    return response.data;
  }

  /** Delete a template */
  async deleteReportTemplate(templateId: string): Promise<void> {
    await this.api.delete(`/reports/templates/${templateId}`);
  }

  // ===== Report Export API =====

  /** Request PDF export for a report */
  async requestPdfExport(reportId: string): Promise<void> {
    await this.api.post(`/reports/${reportId}/export/pdf`);
  }

  /** Request CSV export for a report */
  async requestCsvExport(reportId: string): Promise<void> {
    await this.api.post(`/reports/${reportId}/export/csv`);
  }

  /** Get PDF download URL */
  async getPdfDownloadUrl(reportId: string): Promise<{ download_url: string }> {
    const response = await this.api.get(`/reports/${reportId}/export/pdf/download`);
    return response.data;
  }

  /** Get CSV download URL */
  async getCsvDownloadUrl(reportId: string): Promise<{ download_url: string }> {
    const response = await this.api.get(`/reports/${reportId}/export/csv/download`);
    return response.data;
  }

  /** Download PDF file as a Blob (for email attachment) */
  async downloadReportPdfBlob(reportId: string): Promise<Blob> {
    const response = await this.api.get(`/reports/${reportId}/export/pdf/file`, {
      responseType: 'blob',
    });
    return response.data;
  }

  // ===== Estimates API =====

  /** Get all estimates for a company */
  async getEstimatesByCompany(): Promise<import('@/types').EstimateListDto[]> {
    const response = await this.api.get('/estimates/all');
    return response.data;
  }

  /** Search estimates with filtering, sorting, and pagination */
  async searchEstimates(data: import('@/types').EstimateSearchDTO, companyId?: string): Promise<import('@/types').EstimateSearchResultDTO> {
    const response = await this.api.post('/estimates/search', data, {
      headers: companyId ? { 'x-company-id': companyId } : undefined,
    });
    return response.data;
  }

  /** Get a specific estimate by ID */
  async getEstimateById(estimateId: string): Promise<import('@/types').EstimateDto> {
    const response = await this.api.get(`/estimates/${estimateId}`);
    return response.data;
  }

  /** Create a new estimate */
  async createEstimate(data: import('@/types').CreateEstimateDto): Promise<import('@/types').EstimateDto> {
    const response = await this.api.post('/estimates', data);
    return response.data;
  }

  /** Update an existing estimate */
  async updateEstimate(estimateId: string, data: import('@/types').UpdateEstimateDto): Promise<import('@/types').EstimateDto> {
    const response = await this.api.put(`/estimates/${estimateId}`, data);
    return response.data;
  }

  /** Delete an estimate */
  async deleteEstimate(estimateId: string): Promise<void> {
    await this.api.delete(`/estimates/${estimateId}`);
  }

  /** Convert an estimate to a job */
  async convertEstimateToJob(estimateId: string, data: import('@/types').ConvertEstimateToJobDto): Promise<import('@/types').ConvertEstimateResultDto> {
    const response = await this.api.post(`/estimates/${estimateId}/convert`, data);
    return response.data;
  }

  /** Create an estimate from a job (linked to it). Never deletes the job — call deleteJob for that. */
  async createEstimateFromJob(jobId: string, data: import('@/types').CreateEstimateFromJobDto): Promise<import('@/types').ConvertJobToEstimateResultDto> {
    const response = await this.api.post(`/jobs/${jobId}/estimates`, data);
    return response.data;
  }

  /** Get estimate PDF URL (ensures PDF is current with signature) */
  async getEstimatePdfUrl(estimateId: string): Promise<{ url: string; expires_in: string }> {
    const response = await this.api.get(`/Estimates/${estimateId}/pdf-url`);
    return response.data;
  }

  // ===== Payment Provider API =====

  /** Get payment provider connection status for a company */
  async getPaymentProviderStatus(): Promise<import('@/types').PaymentProviderStatusDTO> {
    const response = await this.api.get(`/connected-accounts/payment-provider`);
    return response.data;
  }

  /** Update payment provider connection for a company */
  async updatePaymentProvider(data: import('@/types').UpdatePaymentProviderDTO): Promise<import('@/types').PaymentProviderStatusDTO> {
    const response = await this.api.put(`/connected-accounts/payment-provider`, data);
    return response.data;
  }

  /** Get a single Stripe payment by ID (for dashboard detail view) */
  async getStripePayment(paymentId: string): Promise<import('@/types').StripePaymentDTO> {
    const response = await this.api.get(`/payments/${paymentId}`);
    return response.data;
  }

  /**
   * Create a PaymentIntent for a job or estimate (lazy / deferred pattern).
   * NO Payment DB row is created — only a Stripe PaymentIntent.
   * The DB row is created exclusively by the Stripe webhook.
   */
  async createPaymentIntent(target: import('@/types').PaymentTarget, amount: number): Promise<{ client_secret: string }> {
    const response = await this.api.post(`/${target.kind === 'job' ? 'jobs' : 'estimates'}/${target.id}/create-intent`, {
      amount,
      currency: 'usd',
    });
    return response.data;
  }

  /**
   * Create a Terminal-compatible PaymentIntent for Tap to Pay.
   * This must allow the `card_present` payment method type.
   */
  async createTerminalPaymentIntent(target: import('@/types').PaymentTarget, amount: number): Promise<{ client_secret: string }> {
    const response = await this.api.post(`/${target.kind === 'job' ? 'jobs' : 'estimates'}/${target.id}/create-intent`, {
      amount,
      currency: 'usd',
      for_terminal: true,
    });
    return response.data;
  }

  /**
   * Create a PaymentIntent for an individual (job-less) payment.
   * Calls DispatchPayments directly — no DispatchData involvement.
   */
  async createIndividualPaymentIntent(companyId: string, amount: number, notes?: string): Promise<{ client_secret: string }> {
    const response = await this.api.post('/payments/create-intent', {
      company_id: companyId,
      amount_in_cents: Math.round(amount * 100),
      currency: 'usd',
      notes,
    });
    return response.data;
  }

  /** Initiate Stripe Connect (OAuth) for a company */
  async initiateStripeConnect(redirectUri: string): Promise<import('@/types').StripeConnectResponseDTO> {
    const response = await this.api.post(`/connected-accounts/stripe/connect`, { redirect_uri: redirectUri });
    return response.data;
  }

  /** Complete Stripe OAuth after redirect — exchanges code for account connection */
  async completeStripeOAuth(
    code: string,
    state: string,
    companyId?: string
  ): Promise<import('@/types').PaymentProviderStatusDTO> {
    const orgId = this.getOrgId();
    const response = await this.api.post(
      `/connected-accounts/stripe/oauth/complete`,
      { code, state },
      {
        headers: {
          ...(companyId ? { 'x-company-id': companyId } : {}),
          ...(orgId ? { 'x-org-id': orgId } : {}),
        },
      }
    );
    return response.data;
  }

  /** Disconnect Stripe account */
  async disconnectStripe(): Promise<void> {
    await this.api.post(`/connected-accounts/stripe/disconnect`);
  }

  /** Manually check/refresh Stripe account status */
  async checkStripeStatus(): Promise<import('@/types').PaymentProviderStatusDTO> {
    const response = await this.api.post(`/connected-accounts/stripe/check-status`);
    return response.data;
  }

  // ===== Payouts API =====

  async getPayoutAccess(): Promise<import('@/types').PayoutAccessDTO> {
    const response = await this.api.get('/payouts/access');
    return response.data;
  }

  async refreshPayoutAccess(): Promise<import('@/types').PayoutAccessDTO> {
    const response = await this.api.post('/payouts/access/refresh');
    return response.data;
  }

  async getStripeBalance(): Promise<import('@/types').StripeBalanceDTO> {
    const response = await this.api.get('/payouts/balance');
    return response.data;
  }

  async listPayouts(params?: { status?: string; limit?: number; starting_after?: string }): Promise<import('@/types').PayoutListResponseDTO> {
    const response = await this.api.get('/payouts', { params });
    return response.data;
  }

  async createPayout(data: { amount: number; currency: string; method?: string; description?: string }): Promise<import('@/types').PayoutDTO> {
    const response = await this.api.post('/payouts', data);
    return response.data;
  }

  async getPayoutSchedule(): Promise<import('@/types').PayoutScheduleDTO> {
    const response = await this.api.get('/payouts/schedule');
    return response.data;
  }

  async updatePayoutSchedule(data: { interval: string; weekly_anchor?: string; monthly_anchor?: number }): Promise<import('@/types').PayoutScheduleDTO> {
    const response = await this.api.put('/payouts/schedule', data);
    return response.data;
  }

  async getPayoutTransactions(payoutId: string, params?: { limit?: number; starting_after?: string }): Promise<import('@/types').PayoutTransactionListDTO> {
    const response = await this.api.get(`/payouts/${payoutId}/transactions`, { params });
    return response.data;
  }


  // ===== Payment Dashboard API =====

  /** Get payment dashboard summary for a company */
  async getPaymentDashboardSummary(
    dateFrom?: string,
    dateTo?: string
  ): Promise<import('@/types').PaymentDashboardSummaryDTO> {
    const params: Record<string, any> = {};
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    const response = await this.api.get('/payments/dashboard/summary', { params });
    return response.data;
  }

  /** Get paginated transaction list */
  async getPaymentTransactions(
    request: import('@/types').TransactionListRequest
  ): Promise<import('@/types').TransactionListResponse> {
    const params: Record<string, any> = {
      page: request.page || 1,
      pageSize: request.page_size || 25
    };
    if (request.date_from) params.dateFrom = request.date_from;
    if (request.date_to) params.dateTo = request.date_to;
    if (request.status) params.status = request.status;
    if (request.payment_method_type) params.paymentMethodType = request.payment_method_type;
    const response = await this.api.get('/payments/dashboard/transactions', { params });
    return response.data;
  }

  /** Get refunds for a specific payment */
  async getPaymentRefunds(paymentId: string): Promise<import('@/types').StripeRefundDTO[]> {
    const response = await this.api.get(`/payments/${paymentId}/refunds`);
    return response.data;
  }

  /** Issue a refund for a Stripe payment (full or partial) */
  async refundPayment(
    paymentId: string,
    request: { amount_in_cents?: number; reason?: string }
  ): Promise<import('@/types').StripeRefundDTO> {
    const response = await this.api.post(`/payments/${paymentId}/refund`, request);
    return response.data;
  }

  /** Manually refresh fee for a Stripe payment (used when fee was delayed for Link/Apple Pay) */
  async refreshPaymentFee(paymentId: string): Promise<import('@/types').StripePaymentDTO> {
    const response = await this.api.post(`/payments/${paymentId}/refresh-fee`);
    return response.data;
  }

  /**
   * Create a Stripe Terminal connection token for the current company.
   * Used by the Tap to Pay flow to initialize the Stripe Terminal SDK.
   */
  async createTerminalConnectionToken(locationId?: string): Promise<{ secret: string }> {
    const response = await this.api.post('/terminal/connection-token', locationId ? { location_id: locationId } : {});
    return response.data;
  }

  /**
   * Ensures a Stripe Terminal location exists for the current company's connected account.
   * Creates one lazily on first call and caches the ID in the database.
   */
  async ensureTerminalLocation(): Promise<{ location_id: string }> {
    const response = await this.api.post('/terminal/ensure-location');
    return response.data;
  }

  // ===== Manual (Non-Stripe) Payments API =====

  /** Search manual (non-provider) payments with filtering and aggregation */
  async getManualPayments(
    request: import('@/types').ManualPaymentSearchRequest
  ): Promise<import('@/types').ManualPaymentSearchResultDTO> {
    const response = await this.api.post('/manual-payments', {
      ...request,
    });
    return response.data;
  }

  // ===== Messaging API =====

  /** Get conversations for a company (inbox) */
  async getConversations(page = 1, pageSize = 20): Promise<import('@/types').ConversationListResponse> {
    const response = await this.api.get('/messaging/conversations', {
      params: { page, page_size: pageSize }
    });
    return response.data;
  }

  /** Get messages for a conversation with cursor-based pagination */
  async getMessages(conversationId: string, cursor?: string, limit = 50): Promise<import('@/types').CursorPaginatedResult<import('@/types').MessageDTO>> {
    const response = await this.api.get(`/messaging/conversations/${conversationId}/messages`, {
      params: { cursor, limit }
    });
    return response.data;
  }

  /** Send a message to a conversation */
  async sendMessage(conversationId: string, body: string): Promise<import('@/types').MessageDTO> {
    const response = await this.api.post(`/messaging/conversations/${conversationId}/messages`, { body });
    return response.data;
  }

  /** Start a new conversation (or send to existing one) */
  async startConversation(companyId: string, technicianId: string, body: string): Promise<import('@/types').MessageDTO> {
    const response = await this.api.post('/messaging/conversations', {
      company_id: companyId,
      technician_id: technicianId,
      body
    });
    return response.data;
  }

  /** Mark a conversation as read */
  async markConversationAsRead(conversationId: string): Promise<void> {
    await this.api.post(`/messaging/conversations/${conversationId}/read`);
  }

  /** Soft-delete a message from a conversation */
  async deleteMessage(conversationId: string, messageId: string): Promise<void> {
    await this.api.delete(`/messaging/conversations/${conversationId}/messages/${messageId}`);
  }

  // ===== VoIP API =====

  /** Get VoIP user for the organization */
  async getVoipUser(): Promise<import('@/types').VoipUserDTO> {
    const response = await this.api.get('/voip/users');
    return response.data;
  }

  /** Create VoIP user credentials */
  async createVoipUser(data: import('@/types').CreateVoipUserDTO): Promise<{ id: string; apiUsername: string }> {
    const response = await this.api.post('/voip/users', data);
    return response.data;
  }

  /** Update VoIP user credentials */
  async updateVoipUser(userId: string, data: import('@/types').UpdateVoipUserDTO): Promise<{ id: string; apiUsername: string }> {
    const response = await this.api.put(`/voip/users/${userId}`, data);
    return response.data;
  }

  /** Delete VoIP user */
  async deleteVoipUser(userId: string): Promise<void> {
    await this.api.delete(`/voip/users/${userId}`);
  }

  /** Get all VoIP numbers for the organization */
  async getVoipNumbers(): Promise<import('@/types').VoipNumberDTO[]> {
    const response = await this.api.get('/voip/numbers');
    return response.data;
  }

  /** Add a VoIP number */
  async addVoipNumber(data: import('@/types').CreateVoipNumberDTO): Promise<import('@/types').VoipNumberDTO> {
    const response = await this.api.post('/voip/numbers', data);
    return response.data;
  }

  /** Update a VoIP number (change company assignment or nickname) */
  async updateVoipNumber(number: string, data: { companyId?: string; nickname?: string }): Promise<import('@/types').VoipNumberDTO> {
    const response = await this.api.put(`/voip/numbers/${number}`, data);
    return response.data;
  }

  /** Delete a VoIP number */
  async deleteVoipNumber(number: string): Promise<void> {
    await this.api.delete(`/voip/numbers/${number}`);
  }

  /** Get timezone offset as integer string (e.g., "-5", "+2") for VoIP.ms (see backend int.TryParse). */
  private getTimezoneOffset(): string {
    return getVoipXTimezoneHeaderValue({ companyIana: this.companyTimezoneIana });
  }

  /** Get VoIP conversations (grouped by contact) */
  async getVoipConversations(did?: string, daysBack: number = 3): Promise<{ conversations: import('@/types').VoipConversationDTO[] }> {
    const params: Record<string, any> = { daysBack };
    if (did) params.did = did;
    const response = await this.api.get('/voip/conversations', {
      params,
      headers: { 'X-Timezone': this.getTimezoneOffset() }
    });
    return response.data;
  }

  /** Get messages for a VoIP conversation */
  async getVoipMessages(did: string, contact: string, daysBack: number = 7): Promise<{
    messages: import('@/types').VoipMessageDTO[];
    daysBack: number;
    hasMoreMessages: boolean;
    fromDate: string;
    toDate: string;
  }> {
    const response = await this.api.get('/voip/conversations/messages', {
      params: { did, contact, daysBack },
      headers: { 'X-Timezone': this.getTimezoneOffset() }
    });
    return response.data;
  }

  /** Mark a VoIP conversation as read */
  async markVoipConversationAsRead(did: string, contact: string): Promise<void> {
    await this.api.post(`/voip/conversations/${did}/${contact}/read`, null, {
      headers: { 'X-Timezone': this.getTimezoneOffset() }
    });
  }

  /** Send an SMS message */
  async sendVoipSms(did: string, dst: string, message: string): Promise<void> {
    await this.api.post('/voip/sms', null, { params: { did, dst, message } });
  }

  /** Send an MMS message */
  async sendVoipMms(data: { did: string; dst: string; message: string; mediaUrl?: string }): Promise<void> {
    await this.api.post('/voip/mms', data);
  }

  // ===== Provider-agnostic Phone Number API (Twilio + VoipMs) =====

  /** Get all phone numbers (Twilio + VoipMs) for the company */
  async getPhoneNumbers(jobId?: string): Promise<import('@/types').PhoneNumberDTO[]> {
    // A technician only gets the numbers technicians can use; with jobId, that job's line comes first.
    const response = await this.api.get('/voip/phone-numbers', { params: jobId ? { job_id: jobId } : undefined });
    return response.data;
  }

  /** Register a new Twilio phone number */
  async registerTwilioNumber(data: import('@/types').RegisterTwilioNumberRequest): Promise<import('@/types').PhoneNumberDTO> {
    const response = await this.api.post('/voip/phone-numbers', data);
    return response.data;
  }

  /** Update a phone number's display name, capabilities, or active status */
  async updatePhoneNumber(id: string, data: import('@/types').UpdatePhoneNumberRequest): Promise<import('@/types').PhoneNumberDTO> {
    const response = await this.api.patch(`/voip/phone-numbers/${id}`, data);
    return response.data;
  }

  /** Delete (or deactivate) a phone number */
  async deletePhoneNumber(id: string, confirm = false): Promise<{ hardDeleted: boolean; softDeleted: boolean; message: string }> {
    const response = await this.api.delete(`/voip/phone-numbers/${id}`, { params: confirm ? { confirm: true } : undefined });
    return response.data;
  }

  /** Configure the Twilio voice webhook for a single phone number (call masking) */
  async configureVoiceWebhook(id: string): Promise<{ message: string }> {
    const response = await this.api.post(`/voip/phone-numbers/${id}/configure-voice-webhook`);
    return response.data;
  }

  /** Companies (besides the owner) this number is shared with. */
  async getNumberSharedCompanies(id: string): Promise<string[]> {
    const response = await this.api.get(`/voip/phone-numbers/${id}/access`);
    return response.data.companyIds ?? [];
  }

  /** Share this number with another company in the org (owner only). */
  async shareNumberWithCompany(id: string, companyId: string): Promise<void> {
    await this.api.post(`/voip/phone-numbers/${id}/access`, { companyId });
  }

  /** Revoke a company's shared access to this number (owner only). */
  async unshareNumberFromCompany(id: string, companyId: string, confirm = false): Promise<void> {
    await this.api.delete(`/voip/phone-numbers/${id}/access/${companyId}`, { params: confirm ? { confirm: true } : undefined });
  }

  /**
   * Register a Telnyx phone number and wire it to the TeXML App + Messaging Profile.
   * Goes through the telnyx-admin controller (requires voip.write), unlike the Twilio register
   * which posts to the provider-agnostic /voip/phone-numbers route.
   *
   * @deprecated No caller — the manual registration form was removed from VoipSettingsPage.
   * Buy numbers with {@link purchasePhoneNumber}; share one with a sibling company using
   * {@link shareNumberWithCompany}. The endpoint is still routed as a recovery path.
   */
  async registerTelnyxNumber(data: import('@/types').RegisterTelnyxNumberRequest): Promise<import('@/types').RegisterTelnyxNumberResponse> {
    const response = await this.api.post('/voip/telnyx-admin/numbers', data);
    return response.data;
  }

  /** US local numbers available to buy. Pass either an area code, or both city and state. */
  async searchAvailableNumbers(params: { areaCode?: string; city?: string; state?: string; limit?: number }): Promise<import('@/types').AvailableNumberDTO[]> {
    const response = await this.api.get('/voip/telnyx-admin/numbers/available', { params });
    return response.data.numbers ?? [];
  }

  /**
   * Buy a number. Resolves with `number` set when Telnyx provisions it inline (the usual case),
   * or with only `orderId` when it is still provisioning — poll getPhoneNumberOrder then.
   */
  async purchasePhoneNumber(
    e164Number: string,
    displayName?: string,
    defaultForwardingNumber?: string,
    dlc10CampaignId?: string,
  ): Promise<import('@/types').PurchasePhoneNumberResponse> {
    const response = await this.api.post('/voip/telnyx-admin/numbers/purchase', {
      e164Number,
      displayName,
      defaultForwardingNumber,
      // '' is not a valid Guid on the server and would fail the whole purchase.
      dlc10CampaignId: dlc10CampaignId || undefined,
    });
    return response.data;
  }

  // ── 10DLC (business texting registration, org admins) ──────────────────────
  // Organization-scoped: every write answers with the fresh status.

  async getDlc10Status(): Promise<import('@/types').Dlc10Status> {
    const response = await this.api.get('/voip/dlc10/status');
    return response.data;
  }

  /** The organization's one business registration and its campaign, covering every company. */
  async registerDlc10(payload: {
    brand: import('@/types').Dlc10BrandForm;
    campaign: import('@/types').Dlc10CampaignForm;
  }): Promise<import('@/types').Dlc10Status> {
    const response = await this.api.post('/voip/dlc10/register', payload);
    return response.data;
  }

  async resubmitDlc10Brand(id: string, form: import('@/types').Dlc10BrandForm): Promise<import('@/types').Dlc10Status> {
    const response = await this.api.put(`/voip/dlc10/brands/${id}`, form);
    return response.data;
  }

  async resubmitDlc10Campaign(id: string, form: import('@/types').Dlc10CampaignForm): Promise<import('@/types').Dlc10Status> {
    const response = await this.api.put(`/voip/dlc10/campaigns/${id}`, form);
    return response.data;
  }

  /** Register a number (or retry) on the organization's campaign or the shared registration. */
  async setNumberDlc10Campaign(
    numberId: string,
    target: { campaignId?: string; useShared?: boolean },
  ): Promise<import('@/types').Dlc10Status> {
    const response = await this.api.post(`/voip/dlc10/numbers/${numberId}/campaign`, target);
    return response.data;
  }

  /** Poll an in-flight number order. */
  async getPhoneNumberOrder(orderId: string): Promise<import('@/types').PhoneNumberOrderDTO> {
    const response = await this.api.get(`/voip/telnyx-admin/numbers/orders/${orderId}`);
    return response.data;
  }

  /** Give a number back to Telnyx, permanently. Owner company only. */
  async releasePhoneNumber(id: string, confirm = false): Promise<{ released: boolean }> {
    const response = await this.api.post(`/voip/telnyx-admin/numbers/${id}/release`, undefined, { params: confirm ? { confirm: true } : undefined });
    return response.data;
  }

  /** The organization's phone-number allowance and usage (org-wide, not per company). */
  async getPhoneNumberQuota(): Promise<import('@/types').PhoneNumberQuotaDTO> {
    const response = await this.api.get('/voip/phone-numbers/quota');
    return response.data;
  }

  /** Re-apply TeXML App + Messaging Profile assignment to every active Telnyx number in the org */
  async reconfigureTelnyxWebhooks(): Promise<{ configured: number }> {
    const response = await this.api.post('/voip/telnyx-admin/numbers/reconfigure');
    return response.data;
  }

  /** Get conversations (provider-agnostic, cursor-paginated) */
  async getConversationsV2(phoneNumberId?: string, limit: number = 25, afterId?: string, jobId?: string): Promise<{
    items: import('@/types').ConversationDTOv2[];
    hasMore: boolean;
  }> {
    const params: Record<string, any> = { limit };
    if (phoneNumberId) params.phoneNumberId = phoneNumberId;
    if (afterId) params.afterId = afterId;
    if (jobId) params.job_id = jobId;
    const response = await this.api.get('/voip/conversations', { params });
    return response.data;
  }

  /** Get messages for a conversation (cursor-paginated, newest-first) */
  async getConversationMessagesV2(conversationId: string, limit: number = 50, beforeId?: string, jobId?: string): Promise<{
    items: import('@/types').MessageDTOv2[];
    hasMore: boolean;
  }> {
    const params: Record<string, any> = { limit };
    if (beforeId) params.beforeId = beforeId;
    if (jobId) params.job_id = jobId;
    const response = await this.api.get(`/voip/conversations/${conversationId}/messages`, { params });
    return response.data;
  }

  /** Resolve the conversation context for a job contact (used by JobContactConversationModal) */
  async resolveConversation(
    data: import('@/types').ResolveConversationRequest
  ): Promise<import('@/types').ResolveConversationResponse> {
    const response = await this.api.post('/voip/conversations/resolve', data);
    return response.data;
  }

  /** Mark a conversation as read */
  async markConversationReadV2(conversationId: string): Promise<void> {
    await this.api.post(`/voip/conversations/${conversationId}/read`);
  }

  /** Send SMS/MMS via provider-agnostic endpoint.
   *  Quota / validation failures come back as HTTP 429/400/422 with `{ error }`; map them onto
   *  the status field the rest of the UI already branches on so callers see the server reason. */
  async sendMessageV2(data: import('@/types').SendMessageV2Request): Promise<import('@/types').SendMessageV2Response> {
    try {
      const response = await this.api.post('/voip/messages', data);
      return response.data;
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number; data?: Record<string, unknown> } })?.response?.status;
      const body = (err as { response?: { data?: Record<string, unknown> } })?.response?.data;
      if (status === 429) {
        return { messageId: '', status: 'quota_blocked', reason: String(body?.error ?? 'Monthly outbound quota exhausted') };
      }
      if (status === 400) {
        return { messageId: '', status: 'invalid', reason: String(body?.error ?? 'Invalid request') };
      }
      if (status === 422) {
        return {
          messageId: String(body?.messageId ?? ''),
          status: 'failed',
          errorCode: typeof body?.errorCode === 'number' ? body.errorCode : undefined,
          errorMessage: body?.error != null ? String(body.error) : undefined,
        };
      }
      throw err;
    }
  }

  /** Prepare presigned S3 POST URLs for outbound MMS file uploads. */
  async prepareMmsMediaUpload(fileNames: string[]): Promise<MmsPrepareUploadResponse[]> {
    const response = await this.api.post('/voip/media/prepare-upload', { fileNames });
    return response.data;
  }

  // ===== Voice Call API =====

  /** Get a short-lived Twilio Access Token for the Capacitor Voice SDK. Requires voip.call permission.
   *  Pass jobId to encode it in the Twilio identity so the TwiML webhook can link the call to the job. */
  async getVoiceToken(jobId?: string): Promise<import('@/types').VoiceTokenResponse> {
    const response = await this.api.post('/voip/voice/token', null, jobId ? { params: { job_id: jobId } } : undefined);
    return response.data;
  }

  /** Server-side hangup fallback: end the caller's in-progress outbound call via the provider REST
   *  API. Used by the softphone when its websocket died and a BYE can't be delivered — without this
   *  the destination keeps ringing a call the device can no longer control. */
  async hangupActiveVoiceCall(context?: { orgId: string; companyId: string }): Promise<{ hungUp: boolean; reason?: string }> {
    if (!context) {
      const response = await this.api.post('/voip/voice/hangup');
      return response.data;
    }
    // Axios' transport timeout doesn't include Firebase/auth interceptor waits. Abort + race the
    // whole operation so a late auth refresh cannot dispatch this against a subsequent call.
    const controller = new AbortController();
    this.voiceCleanupContexts.set(controller.signal, context);
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      return await Promise.race([
        this.api.post('/voip/voice/hangup', undefined, {
          signal: controller.signal,
          timeout: 10_000,
          headers: { 'x-org-id': context.orgId, 'x-company-id': context.companyId },
        }).then((response) => response.data),
        new Promise<never>((_, reject) => {
          timer = setTimeout(() => {
            controller.abort();
            reject(new axios.CanceledError('Call cleanup timed out.'));
          }, 10_000);
        }),
      ]);
    } finally {
      if (timer) clearTimeout(timer);
      this.voiceCleanupContexts.delete(controller.signal);
    }
  }

  // ===== Web dialer (inbound calls to the browser; Telnyx desktop web only) =====

  /**
   * Register this browser tab to receive inbound calls: mints a per-tab Telnyx credential + JWT.
   * endUrl/endToken power the tab-close beacon (preflight-free sendBeacon to the public host) —
   * the authenticated keepalive DELETE needs a CORS preflight that browsers drop at unload.
   */
  async createWebDialerSession(): Promise<{
    sessionId: string; token: string; sipUsername: string; identity: string; ttl: number;
    endUrl?: string; endToken?: string;
  }> {
    const response = await this.api.post('/voip/voice/web-sessions');
    return response.data;
  }

  /** Keeps the session live for ring plans. A 404 means the session was evicted/expired — recreate it. */
  async webDialerHeartbeat(sessionId: string): Promise<void> {
    await this.api.post(`/voip/voice/web-sessions/${sessionId}/heartbeat`);
  }

  /** Fresh JWT for the session. `rotated: true` means the SIP username changed — reconnect required. */
  async refreshWebDialerToken(sessionId: string): Promise<{ token: string; sipUsername: string; rotated: boolean }> {
    const response = await this.api.post(`/voip/voice/web-sessions/${sessionId}/refresh-token`);
    return response.data;
  }

  /** End the session (normal path, e.g. receive mode turned off). */
  async endWebDialerSession(sessionId: string): Promise<void> {
    await this.api.delete(`/voip/voice/web-sessions/${sessionId}`);
  }

  /**
   * Self-service fallback: end every OTHER live session of the current user (stale sessions of
   * closed tabs hog ring/cap slots until the liveness window expires). `keep` = this tab's
   * session, which survives; the pushed stand-down event spares it via kept_session_id.
   */
  async endOtherWebDialerSessions(keepSessionId: string): Promise<{ endedCount: number; notified: boolean }> {
    const response = await this.api.post(`/voip/voice/web-sessions/end-others?keep=${encodeURIComponent(keepSessionId)}`);
    return response.data;
  }

  /**
   * Tab-close variant: `fetch` with `keepalive` survives page unload AND can carry the Authorization
   * header (`navigator.sendBeacon` cannot). MUST stay fully synchronous up to the fetch call —
   * pagehide handlers get one turn; work queued after an `await` is commonly cancelled and the
   * DELETE would never leave the browser (hence the cached token, not a fresh Firebase one).
   * Fire-and-forget by design — if it's lost, the backend's 5-minute liveness window and cleanup
   * job bound the damage.
   */
  endWebDialerSessionKeepalive(sessionId: string): void {
    const token = this.lastAuthToken;
    if (!token) return;
    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
      'x-company-id': this.companyId ?? 'global',
    };
    if (this.orgId) headers['x-org-id'] = this.orgId;
    try {
      void fetch(`${API_BASE_URL}/voip/voice/web-sessions/${sessionId}`, {
        method: 'DELETE',
        keepalive: true,
        headers,
      }).catch(() => { /* best effort */ });
    } catch { /* best effort */ }
  }

  /** Is the current user a ring target on any DID (web routing mode) in the org? */
  async getInboundRoutingMe(): Promise<{ isRingTarget: boolean }> {
    const response = await this.api.get('/voip/inbound-routing/me');
    return response.data;
  }

  /**
   * Active org members eligible as ring targets (the routing panel's picker). Served under
   * voip.inbound-routing.read — NOT users.list, which Secretaries holding the routing feature
   * typically lack (review fix: without a picker they could never change who rings).
   */
  async getInboundRoutingTargets(): Promise<{
    // companyIds = the companies this member may work in; null = all of them (an Admin), whose
    // stored company list is never consulted. Used to scope a number's ring-target picker to the
    // companies that number is shared with.
    items: Array<{ id: string; name: string | null; email: string | null; companyIds: string[] | null }>;
  }> {
    const response = await this.api.get('/voip/inbound-routing/targets');
    return response.data;
  }

  /**
   * Live (ring-eligible) web-dialer sessions per user across the org — feeds the routing panel's
   * "who is receiving" view and its over-cap warning.
   */
  async getInboundRoutingLiveSessions(): Promise<{
    items: Array<{ userId: string; sessionCount: number; lastSeenAt: string }>;
  }> {
    const response = await this.api.get('/voip/inbound-routing/live-sessions');
    return response.data;
  }

  /**
   * Admin-only (voip.write — FixedDeny for non-admin roles): force-end ALL of a user's receiving
   * browser tabs. The kicked tabs stand down via a realtime push to that user's tabs and stay
   * unregistered until reloaded. `notified: false` = the push could not be delivered, so live
   * tabs may self-heal back within a minute — the caller should warn and offer a retry.
   */
  async disconnectWebDialerTargetSessions(userId: string): Promise<{ endedCount: number; notified: boolean }> {
    const response = await this.api.delete(`/voip/inbound-routing/targets/${userId}/sessions`);
    return response.data;
  }

  /** Per-DID inbound routing configs for the company's Telnyx voice numbers. */
  async getInboundRoutingConfigs(): Promise<{
    items: Array<{
      phoneNumberId: string; e164Number: string; displayName: string | null;
      /** WebOnly = "Forward to Web Dialer User". The two web+phone modes are pre-option settings kept as they were. */
      mode: 'FallbackOnly' | 'WebAndFallback' | 'WebThenFallback' | 'WebOnly';
      webRingTimeoutSeconds: number; defaultForwardingNumber: string | null; targetUserIds: string[];
      /** Owning company + every company the number is actively shared with. */
      companyIdsWithAccess: string[];
      /** Any of these non-null = that option is in effect and the fields above are not. */
      callFlowId?: string | null;
      callFlowName?: string | null;
      /** For a SIP account this is the account's name, never its address. */
      sipUri?: string | null;
      /** The number forwards to a SIP account ("Forward to SIP account"). */
      isSipAccount?: boolean;
      ringGroupId?: string | null;
      ringGroupName?: string | null;
    }>;
  }> {
    const response = await this.api.get('/voip/inbound-routing');
    return response.data;
  }

  // ── Call flows (company-scoped: x-company-id must be a real company) ──────

  async getCallFlows(): Promise<import('@/types').CallFlowDTO[]> {
    return (await this.api.get('/voip/call-flows')).data.items;
  }

  async createCallFlow(body: { name: string; branches: import('@/types').CallFlowBranch[]; defaultSteps: import('@/types').CallFlowStep[] }): Promise<import('@/types').CallFlowDTO> {
    return (await this.api.post('/voip/call-flows', body)).data;
  }

  async updateCallFlow(id: string, body: { name: string; branches: import('@/types').CallFlowBranch[]; defaultSteps: import('@/types').CallFlowStep[] }): Promise<import('@/types').CallFlowDTO> {
    return (await this.api.put(`/voip/call-flows/${id}`, body)).data;
  }

  async deleteCallFlow(id: string): Promise<void> {
    await this.api.delete(`/voip/call-flows/${id}`);
  }

  async duplicateCallFlow(id: string): Promise<import('@/types').CallFlowDTO> {
    return (await this.api.post(`/voip/call-flows/${id}/duplicate`)).data;
  }

  async getRingGroups(): Promise<import('@/types').RingGroupDTO[]> {
    return (await this.api.get('/voip/call-flows/ring-groups')).data.items;
  }

  async createRingGroup(body: { name: string; members: import('@/types').RingGroupMember[] }): Promise<import('@/types').RingGroupDTO> {
    return (await this.api.post('/voip/call-flows/ring-groups', body)).data;
  }

  async updateRingGroup(id: string, body: { name: string; members: import('@/types').RingGroupMember[] }): Promise<import('@/types').RingGroupDTO> {
    return (await this.api.put(`/voip/call-flows/ring-groups/${id}`, body)).data;
  }

  async deleteRingGroup(id: string): Promise<void> {
    await this.api.delete(`/voip/call-flows/ring-groups/${id}`);
  }

  async getSavedDestinations(): Promise<import('@/types').SavedDestinationDTO[]> {
    return (await this.api.get('/voip/call-flows/destinations')).data.items;
  }

  async createSavedDestination(body: { name: string; type: 'number' | 'sip'; value: string }): Promise<import('@/types').SavedDestinationDTO> {
    return (await this.api.post('/voip/call-flows/destinations', body)).data;
  }

  /** Rewrites every flow step, ring-group member and number linked to it. */
  async updateSavedDestination(id: string, body: { name: string; value: string }): Promise<import('@/types').SavedDestinationDTO> {
    return (await this.api.put(`/voip/call-flows/destinations/${id}`, body)).data;
  }

  async deleteSavedDestination(id: string): Promise<void> {
    await this.api.delete(`/voip/call-flows/destinations/${id}`);
  }

  /** userName is the admin's part; the server adds the org's suffix. Rename with updateSavedDestination (its value is ignored). */
  async createSipAccount(body: { name: string; userName: string; password: string }): Promise<{
    destination: import('@/types').SavedDestinationDTO; setup: import('@/types').SipAccountSetupDTO;
  }> {
    return (await this.api.post('/voip/call-flows/destinations/sip-account', body)).data;
  }

  /** A POST on purpose: showing the password needs edit rights. */
  async getSipAccountSetup(id: string): Promise<import('@/types').SipAccountSetupDTO> {
    return (await this.api.post(`/voip/call-flows/destinations/${id}/sip-account/details`)).data;
  }

  async setSipAccountPassword(id: string, password: string): Promise<import('@/types').SipAccountSetupDTO> {
    return (await this.api.put(`/voip/call-flows/destinations/${id}/sip-account/password`, { password })).data;
  }

  async getCallSchedules(): Promise<import('@/types').CallScheduleDTO[]> {
    return (await this.api.get('/voip/call-flows/schedules')).data.items;
  }

  async createCallSchedule(body: { name: string; timezone: string | null; weekly: import('@/types').ScheduleWeeklyWindow[]; dates: import('@/types').ScheduleDateEntry[] }): Promise<import('@/types').CallScheduleDTO> {
    return (await this.api.post('/voip/call-flows/schedules', body)).data;
  }

  async updateCallSchedule(id: string, body: { name: string; timezone: string | null; weekly: import('@/types').ScheduleWeeklyWindow[]; dates: import('@/types').ScheduleDateEntry[] }): Promise<import('@/types').CallScheduleDTO> {
    return (await this.api.put(`/voip/call-flows/schedules/${id}`, body)).data;
  }

  async deleteCallSchedule(id: string): Promise<void> {
    await this.api.delete(`/voip/call-flows/schedules/${id}`);
  }

  /** The flow editor's user picker — the inbound-routing list, under voip.call-flows.read. */
  async getCallFlowTargets(): Promise<import('@/types').RoutingTargetDTO[]> {
    return (await this.api.get('/voip/call-flows/targets')).data.items;
  }

  /** Each company number's flow + time rules, SIP URI or ring group (all null = Phone/Browser). */
  async getCallFlowAssignments(): Promise<import('@/types').NumberCallFlowAssignment[]> {
    return (await this.api.get('/voip/call-flows/phone-numbers')).data.items;
  }

  /**
   * Set a number's routing option. route 'simple' = back to Phone/Browser (which the inbound-routing
   * PUT picks between; those settings were never touched). The other two fields are cleared.
   */
  async assignCallFlow(phoneNumberId: string, body: {
    route: 'simple' | 'sip' | 'ringGroup' | 'flow';
    callFlowId?: string | null; rules?: import('@/types').NumberFlowRule[]; sipUri?: string | null; ringGroupId?: string | null;
    sipDestinationId?: string | null;
  }): Promise<import('@/types').NumberCallFlowAssignment> {
    return (await this.api.put(`/voip/call-flows/phone-numbers/${phoneNumberId}`, body)).data;
  }

  /**
   * Update a DID's inbound routing (owner company only). Broadcasts InboundRoutingChanged
   * org-wide. The response carries the EFFECTIVE config — the server clamps the timeout and drops
   * stale already-configured targets — which callers should adopt as their new saved state.
   */
  async updateInboundRoutingConfig(
    phoneNumberId: string,
    body: { mode: string; webRingTimeoutSeconds: number; targetUserIds: string[] },
  ): Promise<{ phoneNumberId: string; mode: string; webRingTimeoutSeconds: number; targetUserIds: string[] }> {
    const response = await this.api.put(`/voip/inbound-routing/${phoneNumberId}`, body);
    return response.data;
  }

  /** Retrieve paginated call history for the current company. */
  async getCallHistory(params: {
    page?: number;
    page_size?: number;
    phone_number_id?: string;
    search_text?: string;
    date_from?: string;
    date_to?: string;
    job_id?: string;
  } = {}): Promise<import('@/types').CallHistoryResponse> {
    const response = await this.api.get('/voip/voice/calls', { params });
    return response.data;
  }

  /** Returns a short-lived presigned S3 URL for a call recording. */
  async getCallRecordingUrl(callId: string): Promise<{ url: string }> {
    const response = await this.api.get(`/voip/voice/calls/${callId}/recording`);
    return response.data;
  }

  /** Monthly telephony usage & overage cost for the current org. Omit period for the current
   *  Eastern month; otherwise pass YYYYMM. All boundaries are US Eastern (America/New_York). */
  async getTelephonyUsage(period?: number): Promise<import('@/types').TelephonyUsageDTO> {
    const response = await this.api.get('/voip/usage', { params: period ? { period } : undefined });
    return response.data;
  }

  // ===== Platform Billing API (web-only; hidden on native builds) =====

  async getBilling(): Promise<import('@/types').BillingView> {
    const response = await this.api.get('/billing');
    return response.data;
  }

  async getBillingPlans(): Promise<import('@/types').BillingPlan[]> {
    const response = await this.api.get('/billing/plans');
    return response.data;
  }

  /** Quote for the charge-confirmation modal. Does not charge. */
  async previewCharge(
    planKey: string,
    billingPeriod: 'Monthly' | 'Annual',
    action: 'subscribe' | 'change_plan',
  ): Promise<import('@/types').BillingChargePreview> {
    const response = await this.api.post('/billing/charge-preview', {
      plan_key: planKey,
      billing_period: billingPeriod,
      action,
    });
    return response.data;
  }

  /** Start a subscription. Returns { activated } if a card is already on file, else { checkout_url }. */
  async subscribe(planKey: string, billingPeriod: 'Monthly' | 'Annual'): Promise<{ activated: boolean; checkout_url?: string }> {
    const response = await this.api.post('/billing/subscribe', { plan_key: planKey, billing_period: billingPeriod });
    return response.data;
  }

  /** Checkout (setup) URL to add/replace the card without changing the plan (works for custom plans). */
  async createCardSetupSession(): Promise<{ url: string }> {
    const { data } = await this.api.post('/billing/setup-session', {});
    return data;
  }

  async createBillingPortalSession(): Promise<{ url: string }> {
    const response = await this.api.post('/billing/portal-session', {});
    return response.data;
  }

  async changeBillingPlan(
    planKey: string,
    billingPeriod?: 'Monthly' | 'Annual',
  ): Promise<{ applied: boolean; scheduled: boolean; pending_cancelled: boolean; effective_at: string | null; plan_key: string }> {
    const { data } = await this.api.post('/billing/plan', { plan_key: planKey, billing_period: billingPeriod });
    return data;
  }

  async cancelSubscription(): Promise<void> {
    await this.api.post('/billing/cancel', {});
  }

  /** Re-attempt payment for outstanding/failed invoices (e.g. after updating the card). */
  async retryPayment(): Promise<{ retried: number; paid: number; failed: number; error?: string | null }> {
    const { data } = await this.api.post('/billing/retry', {});
    return data;
  }

  // ===== Technician Location / Presence API =====

  async getTechnicianLocations(): Promise<import('@/types').TechnicianLocationDTO[]> {
    const response = await this.api.get('/location/get');
    return (response.data || []).map((loc: Record<string, unknown>) => ({
      technicianId: loc.technician_id ?? loc.technicianId,
      latitude: loc.latitude,
      longitude: loc.longitude,
      accuracy: loc.accuracy,
      updatedAt: loc.updated_at ?? loc.updatedAt,
    })) as import('@/types').TechnicianLocationDTO[];
  }

  // ===== Feature Key / Permissions API =====

  /** Get resolved feature statuses for a company role */
  async getCompanyRoleFeatures(role: string): Promise<import('@/types').FeatureStatusDTO[]> {
    const response = await this.api.get('/authorization/features/company-role', {
      params: { role }
    });
    return response.data;
  }

  /**
   * Org users who currently resolve `voip.call` as allowed (union across their companies) —
   * the routing target picker hides everyone else, since they could never receive a call.
   * Served under voip.inbound-routing.read; the feature key is allow-listed server-side.
   */
  async getVoipReceiveCapableUserIds(): Promise<{ userIds: string[] }> {
    const response = await this.api.get('/authorization/features/users-with-feature', {
      params: { featureKey: 'voip.receive-calls' },
    });
    // DispatchAuthorization serializes snake_case (user_ids). Reading the wrong casing here
    // yielded `new Set(undefined)` = an EMPTY set — a silent "nobody is callable" that hid the
    // whole target picker (live-testing find). An unexpected shape must THROW instead: the
    // panel's catch turns it into "filter unavailable, show everyone", the safe degradation.
    const ids = response.data?.user_ids;
    if (!Array.isArray(ids)) throw new Error('Unexpected users-with-feature response shape');
    return { userIds: ids };
  }

  /** Get resolved feature statuses for a user in a company */
  async getUserFeatures(userId: string): Promise<import('@/types').FeatureStatusDTO[]> {
    const response = await this.api.get('/authorization/features/user', {
      params: { userId }
    });
    return response.data;
  }

  /**
   * Get lean feature key statuses for the current user (uses x-user-id, x-user-role, x-company-id headers).
   *
   * Returns `null` when the server has no feature map for this user. A pre-subscription backend
   * answered `204 No Content` for admins and the UI allowed every feature locally; this build
   * fetches a real map instead, so if it ever runs against a rolled-back backend the 204 must not
   * be read as an *empty* map — that means "every feature denied" and strips the entire admin UI.
   * Callers treat `null` as the legacy allow-all. The current backend never returns 204 here.
   */
  async getMyFeatures(prefix: string = ''): Promise<import('@/types').FeatureKeyStatusDTO[] | null> {
    const response = await this.api.get('/authorization/features/user-features', {
      params: { prefix }
    });
    if (response.status === 204 || response.data === '' || response.data == null) return null;
    // Anything else non-array is a genuine fault (an error page, a proxy interstitial). Throwing
    // keeps TanStack on the last known-good map rather than silently denying everything.
    if (!Array.isArray(response.data)) {
      throw new Error(`Unexpected user-features response body: ${typeof response.data}`);
    }
    return response.data;
  }

  /** Upsert a company-role feature override */
  async upsertCompanyRoleFeatureOverride(data: import('@/types').CompanyRoleFeatureOverrideRequest): Promise<any> {
    const response = await this.api.post('/authorization/overrides/company-role', {
      company_id: data.companyId,
      role: data.role,
      feature_key: data.featureKey,
      allowed: data.allowed
    });
    return response.data;
  }

  /** Upsert a user feature override */
  async upsertUserFeatureOverride(data: import('@/types').UserFeatureOverrideRequest): Promise<any> {
    const response = await this.api.post('/authorization/overrides/user', {
      user_id: data.userId,
      company_id: data.companyId,
      feature_key: data.featureKey,
      allowed: data.allowed
    });
    return response.data;
  }

  // ===== Dashboard API =====

  private dashboardParams(startDate?: string, endDate?: string): Record<string, any> {
    const params: Record<string, any> = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    return params;
  }

  async getDashboardSummary(startDate?: string, endDate?: string): Promise<import('@/types').DashboardSummaryDTO> {
    const response = await this.api.get('/dashboard/summary', { params: this.dashboardParams(startDate, endDate) });
    return response.data;
  }

  async getDashboardTechnicianJobs(startDate?: string, endDate?: string): Promise<import('@/types').TechnicianJobsChartDTO[]> {
    const response = await this.api.get('/dashboard/technician-jobs', { params: this.dashboardParams(startDate, endDate) });
    return response.data;
  }

  async getDashboardPaymentBreakdown(startDate?: string, endDate?: string): Promise<import('@/types').PaymentBreakdownChartDTO[]> {
    const response = await this.api.get('/dashboard/payment-breakdown', { params: this.dashboardParams(startDate, endDate) });
    return response.data;
  }

  async getDashboardExpenseBreakdown(startDate?: string, endDate?: string): Promise<import('@/types').ExpenseBreakdownChartDTO> {
    const response = await this.api.get('/dashboard/expense-breakdown', { params: this.dashboardParams(startDate, endDate) });
    return response.data;
  }

  async getDashboardJobStatusRatio(startDate?: string, endDate?: string): Promise<import('@/types').JobStatusRatioDTO> {
    const response = await this.api.get('/dashboard/job-status-ratio', { params: this.dashboardParams(startDate, endDate) });
    return response.data;
  }

  async getDashboardTechnicianRevenue(startDate?: string, endDate?: string): Promise<import('@/types').TechnicianRevenueDTO[]> {
    const response = await this.api.get('/dashboard/technician-revenue', { params: this.dashboardParams(startDate, endDate) });
    return response.data;
  }

  async getDashboardDailyRevenue(startDate?: string, endDate?: string): Promise<import('@/types').DailyRevenueDTO[]> {
    const response = await this.api.get('/dashboard/daily-revenue', { params: this.dashboardParams(startDate, endDate) });
    return response.data;
  }

  async getDashboardTechnicianProfit(startDate?: string, endDate?: string): Promise<import('@/types').TechnicianProfitDTO[]> {
    const response = await this.api.get('/dashboard/technician-profit', { params: this.dashboardParams(startDate, endDate) });
    return response.data;
  }

  async getDashboardJobsBySource(startDate?: string, endDate?: string): Promise<import('@/types').JobSourceChartDTO[]> {
    const response = await this.api.get('/dashboard/job-source', { params: this.dashboardParams(startDate, endDate) });
    return response.data;
  }

  async getDashboardJobsByJobType(startDate?: string, endDate?: string): Promise<import('@/types').JobTypeChartDTO[]> {
    const response = await this.api.get('/dashboard/job-type', { params: this.dashboardParams(startDate, endDate) });
    return response.data;
  }

  async getDashboardExpensesByCategory(startDate?: string, endDate?: string): Promise<import('@/types').ExpenseCategoryChartDTO[]> {
    const response = await this.api.get('/dashboard/expense-categories', { params: this.dashboardParams(startDate, endDate) });
    return response.data;
  }

  async getDashboardTodaySchedule(limit?: number): Promise<import('@/types').TodayScheduleResponseDTO> {
    const response = await this.api.get('/dashboard/today-schedule', { params: { limit } });
    return response.data;
  }

  async getDashboardOutstandingInvoices(limit?: number): Promise<import('@/types').OutstandingInvoicesResponseDTO> {
    const response = await this.api.get('/dashboard/outstanding-invoices', { params: { limit } });
    return response.data;
  }

  async getDashboardTechnicianBalances(limit?: number): Promise<import('@/types').DashboardTechBalancesResponseDTO> {
    const response = await this.api.get('/dashboard/technician-balances', { params: { limit } });
    return response.data;
  }

  // FCM Push Notifications
  async registerFcmToken(token: string, platform: string = 'android') {
    const response = await this.api.post('/fcm-tokens', { token, platform });
    return response.data;
  }

  async unregisterFcmToken(token: string) {
    const response = await this.api.delete('/fcm-tokens', { data: { token } });
    return response.data;
  }

  // Notification center (per user, current org; spans every company)
  async getNotifications(params: { before?: string; limit?: number; unreadOnly?: boolean } = {}): Promise<import('@/types').AppNotificationPage> {
    const response = await this.api.get('/notifications', {
      params: { before: params.before, limit: params.limit, unreadOnly: params.unreadOnly },
    });
    return response.data;
  }

  async getUnreadNotificationCount(): Promise<number> {
    const response = await this.api.get('/notifications/unread-count');
    return response.data.count;
  }

  async markNotificationRead(id: string): Promise<void> {
    await this.api.post(`/notifications/${id}/read`);
  }

  async markNotificationUnread(id: string): Promise<void> {
    await this.api.post(`/notifications/${id}/unread`);
  }

  async markAllNotificationsRead(): Promise<void> {
    await this.api.post('/notifications/read-all');
  }

  async getNotificationSettings(): Promise<import('@/types').NotificationSettingDTO[]> {
    const response = await this.api.get('/notifications/settings');
    return response.data;
  }

  async updateNotificationSetting(type: string, setting: { enabled: boolean; push: boolean; admins_only: boolean }): Promise<void> {
    await this.api.put(`/notifications/settings/${type}`, setting);
  }

  // Location
  async requestLocationRefresh(technicianUserId: string): Promise<void> {
    await this.api.post(`/location/request-refresh/${technicianUserId}`);
  }
}

export const apiService = new ApiService();


